import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import ComposeTemplateRichEditor from './ComposeTemplateRichEditor';
import GmailAgentCodingBar from './GmailAgentCodingBar';
import CommunicationsOutboundView from './CommunicationsOutboundView';
import './CommunicationsDashboard.css';

function getApiBase() {
  if (import.meta.env.REACT_APP_API_URL) return import.meta.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}
const API = getApiBase();

/** Shown in Compose/Preview; SMTP sends as this display name (see optiprime-email-service). */
const OPTIPRIME_SMTP_FROM_LABEL = 'OptiPrime AI Inc. <contact@optiprime.ai>';

/** Gmail API system `labelIds` — stored in mirror `email_inbox.gmail_labels` JSON */
const GMAIL_SYSTEM_FOLDER_FILTERS = [
  { id: 'INBOX', title: 'Inbox', tone: 'inbox' },
  { id: 'SENT', title: 'Sent', tone: 'sent' },
  { id: 'DRAFT', title: 'Draft', tone: 'draft' },
  { id: 'SPAM', title: 'Spam', tone: 'spam' },
  { id: 'TRASH', title: 'Trash', tone: 'trash' },
  { id: 'STARRED', title: 'Starred', tone: 'starred' },
];

/**
 * Gmail user-label quick filters (excludes parent OPO). Tile text vs API `label_name` from mirror.
 * Matchers run against `/api/communications/labels` names (e.g. OPO/General).
 */
const GMAIL_QUICK_LABEL_PRESETS = [
  { tileTitle: 'GOOGLE', match: (n) => /^GOOGLE$/i.test(n) },
  { tileTitle: 'NVIDIA\nMACHINE', match: (n) => /NVIDEA\s+MACHINE|NVIDIA\s+MACHINE/i.test(n) },
  { tileTitle: 'CIBC', match: (n) => /^OPO\/CIBC$/i.test(n) || /^CIBC$/i.test(n) },
  { tileTitle: 'Daily\nSummary', match: (n) => /^OPO\/Daily Summary$/i.test(n) },
  { tileTitle: 'General', match: (n) => /^OPO\/General$/i.test(n) },
  { tileTitle: 'LinkedIn', match: (n) => /^OPO\/LinkedIn$/i.test(n) },
  { tileTitle: 'New\nSign Up', match: (n) => /^OPO\/New Sign Up$/i.test(n) },
  { tileTitle: 'OptiGuard', match: (n) => /^OPO\/OptiGuard$/i.test(n) },
  { tileTitle: 'OptiLaw\nFeedback', match: (n) => /^OPO\/OptiLaw Feedback$/i.test(n) },
  { tileTitle: 'OptiPrime\nBeta', match: (n) => /^OPO\/OptiPrime Beta/i.test(n) },
  { tileTitle: 'Web\nSummit', match: (n) => /^OPO\/Web Summit$/i.test(n) },
  { tileTitle: 'OptiLaw\nEvaluator', match: (n) => /^OptiLaw Evaluator$/i.test(n) },
  { tileTitle: 'OptiPrime\nAI Setup', match: (n) => /^OptiPrime AI Setup$/i.test(n) },
  { tileTitle: 'WEBSITE', match: (n) => /^WEBSITE$/i.test(n) },
];

/** Max snapshots kept for “Undo” after AI Enhance (name, subject, body per step). */
const AI_ENHANCE_UNDO_MAX = 20;

function adminSessionHeaders() {
  try {
    const t = localStorage.getItem('sessionToken');
    return t ? { 'X-Admin-Session': t } : {};
  } catch {
    return {};
  }
}

/** Avoid cached API responses; include admin session when present. */
function commsFetch(url, options = {}) {
  return fetch(url, {
    cache: 'no-store',
    credentials: 'include',
    ...options,
    headers: { ...adminSessionHeaders(), ...(options.headers || {}) },
  });
}

function formatSyncTimestamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' })} (${d.toISOString()})`;
}

function formatRelativeTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 0) return d.toLocaleTimeString();
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
}

function levelFromMinutes(min, warn, crit) {
  if (min == null || !Number.isFinite(min)) return 'unknown';
  if (min >= crit) return 'critical';
  if (min >= warn) return 'warning';
  return 'good';
}

function deriveCommsLevel(stats) {
  if (!stats) return 'unknown';
  const status = String(stats.syncStatus || '').toLowerCase();
  if (status === 'error' || status === 'failed') return 'critical';
  const lastIso = stats.lastSyncSessionAt || stats.lastFullSync;
  if (!lastIso) return 'warning';
  const ageMin = (Date.now() - new Date(lastIso).getTime()) / 60000;
  // Gmail Monitor cycles every 5 minutes — over 30 min is critical, over 15 min is warning.
  if (ageMin >= 30) return 'critical';
  if (ageMin >= 15) return 'warning';
  if ((stats.pendingInstructions || 0) >= 10) return 'warning';
  return 'good';
}

function levelLabelForState(level) {
  if (level === 'good') return 'RELAY ONLINE';
  if (level === 'warning') return 'RELAY DEGRADED';
  if (level === 'critical') return 'RELAY OFFLINE';
  return 'RELAY INITIALISING';
}

function levelFromPendingInstructions(n) {
  const v = Number(n) || 0;
  if (v >= 10) return 'critical';
  if (v >= 1) return 'warning';
  return 'good';
}

function normalizeUserTypeList(userTypes) {
  if (Array.isArray(userTypes)) return userTypes.map((x) => String(x));
  if (userTypes == null) return [];
  if (typeof userTypes === 'string') {
    try {
      const p = JSON.parse(userTypes);
      return Array.isArray(p) ? p.map(String) : [String(userTypes)];
    } catch {
      return userTypes.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }
  return [String(userTypes)];
}

function userHasBetaRole(u) {
  return normalizeUserTypeList(u.userTypes).some((t) => /beta/i.test(t));
}

function userEmailEligible(u) {
  const email = (u.email || '').trim();
  if (!email.includes('@')) return false;
  const ok = (v) =>
    v === true ||
    v === 1 ||
    v === '1' ||
    (typeof v === 'string' && v.toLowerCase() === 'true') ||
    (typeof v === 'string' && v.toLowerCase() === 't');
  return ok(u.accountEnabled) && ok(u.isActive);
}

const COMPOSE_TEMPLATES_STORAGE_KEY = 'optiprime-admin-compose-templates';

function loadComposeTemplatesFromStorage() {
  try {
    const raw = localStorage.getItem(COMPOSE_TEMPLATES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistComposeTemplatesToStorage(list) {
  try {
    localStorage.setItem(COMPOSE_TEMPLATES_STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* ignore quota / private mode */
  }
}

function parseJsonSafe(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function responseLooksLikeHtml(text) {
  const t = String(text || '').trimStart().slice(0, 240).toLowerCase();
  return t.startsWith('<!doctype') || t.startsWith('<html') || t.startsWith('<!--');
}

/** Avoid showing nginx/Cloudflare HTML error pages in the red alert. */
function formatAiEnhanceFailureMessage(status, responseText, parsedBody) {
  const p = parsedBody && typeof parsedBody === 'object' ? parsedBody : {};
  if (typeof p.error === 'string' && p.error.trim() && !responseLooksLikeHtml(p.error)) {
    const hint = typeof p.hint === 'string' && p.hint.trim() ? ` ${p.hint.trim()}` : '';
    return `${p.error.trim()}${hint}`;
  }
  if (responseLooksLikeHtml(responseText)) {
    return (
      `The server returned a web page instead of JSON (HTTP ${status ?? 'error'}). ` +
      `That usually means a proxy or gateway timed out, blocked the request, or sent the wrong response. ` +
      `Try again shortly. If it keeps failing: restart the OptiQuery backend (port 5001), confirm POST /api/communications/compose-templates/ai-enhance is handled by Node, ` +
      `set OLLAMA_BASE_URL to your DGX Spark Ollama host, and increase nginx/Cloudflare proxy read timeouts (AI runs can take several minutes).`
    );
  }
  const plain = String(responseText || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length > 320) return `${plain.slice(0, 320)}…`;
  if (plain) return plain;
  return `Request failed${status != null ? ` (HTTP ${status})` : ''}.`;
}

/** Load templates from OptiQuery backend (server.js); 404 = backend not redeployed — use localStorage fallback. */
async function fetchComposeTemplatesFromApi() {
  const r = await commsFetch(`${API}/api/communications/compose-templates`);
  if (r.status === 404) {
    console.warn(
      '[Communications] compose-templates API returned 404. Pull latest node-optiquery and restart the OptiQuery backend (PM2 / port 5001) so server.js registers GET/PUT /api/communications/compose-templates. Templates will work from this browser only until then.'
    );
    return { success: false, templates: [], _httpStatus: 404 };
  }
  const text = await r.text();
  const d = parseJsonSafe(text);
  if (!d || typeof d !== 'object') return { success: false, templates: [] };
  return d;
}

function saveComposeTemplatesToServer(templates) {
  return commsFetch(`${API}/api/communications/compose-templates`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ templates }),
  }).then(async (r) => {
    if (r.status === 404) {
      console.warn(
        '[Communications] Saving templates failed: API returned 404 — restart OptiQuery backend (server.js) after deploy.'
      );
      return { success: false, error: 'compose-templates not found (404)' };
    }
    const text = await r.text();
    return parseJsonSafe(text) || { success: false, error: 'Invalid JSON from server' };
  });
}

function newComposeTemplateId() {
  return `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

const COMPLIANCE_PRIORITY_ORDER = ['critical', 'required', 'recommended'];

function shortComplianceSectionLabel(sec) {
  if (!sec) return '';
  if (sec === 'Other linked documents') return 'Other linked';
  return sec
    .replace('Resolution to Purchase DGX Spark', 'DGX Spark Resolution')
    .replace('SR&ED (R&D Tax Credits)', 'SR&ED')
    .replace('Corporate Registry and Compliance', 'Corp. Registry')
    .replace('Share Register and Capital', 'Share Register')
    .replace('CRA and Tax Filings', 'CRA & Tax')
    .replace('Receipts and Audit Trail', 'Receipts')
    .replace('Setup and Incorporation', 'Setup & Incorp.')
    .replace('Assets and Depreciation', 'Assets & Depr.')
    .replace('Investor Readiness', 'Investor')
    .replace('Financial Records', 'Financials')
    .replace('Minute Book', 'Minute Book');
}

function normalizeTemplateAttachments(tpl) {
  const raw = tpl && Array.isArray(tpl.attachments) ? tpl.attachments : [];
  return raw.filter((a) => a && a.linkedDocumentId != null);
}

/** Placeholders: (user_firstname), (user_email), … Filled from platform user profile when sending. */
const COMPOSE_MERGE_FIELDS = [
  { token: 'user_firstname', label: 'First name', get: (u) => (u?.firstName || '').trim() },
  { token: 'user_lastname', label: 'Last name', get: (u) => (u?.lastName || '').trim() },
  {
    token: 'user_full_name',
    label: 'Full name',
    get: (u) => [u?.firstName, u?.lastName].filter((x) => x && String(x).trim()).join(' ').trim(),
  },
  {
    token: 'user_display_name',
    label: 'Display name',
    get: (u) => {
      if (!u) return '';
      const full = [u.firstName, u.lastName].filter((x) => x && String(x).trim()).join(' ').trim();
      if (full) return full;
      if (u.username && String(u.username).trim()) return String(u.username).trim();
      const em = (u.email || '').split('@')[0];
      return em || '';
    },
  },
  { token: 'user_email', label: 'Email', get: (u) => (u?.email || '').trim() },
  { token: 'user_username', label: 'Username', get: (u) => (u?.username || '').trim() },
  {
    token: 'user_organization',
    label: 'Organization',
    get: (u) => (u?.organizationName || u?.organizationname || '').trim(),
  },
];

/** Shown in “Preview as recipient” and template card preview so merge tokens look like a real send. */
const COMPOSE_PREVIEW_SAMPLE_USER = {
  firstName: 'Jose',
  lastName: 'Martinez',
  email: 'admin@mayanmechanical.com',
  username: 'jose.mayan',
  organizationName: 'Mayan Mechanical Ltd.',
};

/** Outer shell for iframe preview — dark canvas matches template modal + typical dark reading pane (not a “white Gmail” mat). */
function buildEmailClientPreviewSrcDoc(mergedBodyHtml) {
  const body = String(mergedBodyHtml || '');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head><body style="margin:0;padding:16px 12px;background:#0d1117;font-family:Arial,Helvetica,sans-serif;-webkit-font-smoothing:antialiased;">${body}</body></html>`;
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function applyMergeFieldsToText(text, user) {
  if (text == null || text === '') return text;
  let out = String(text);
  for (const { token, get } of COMPOSE_MERGE_FIELDS) {
    const val = get(user);
    const esc = escapeRegExp(token);
    out = out.replace(new RegExp(`\\(${esc}\\)`, 'gi'), () => val);
    out = out.replace(new RegExp(`\\{${esc}\\}`, 'gi'), () => val);
  }
  return out;
}

function textContainsMergeFields(text) {
  if (!text) return false;
  return COMPOSE_MERGE_FIELDS.some(({ token }) => {
    const esc = escapeRegExp(token);
    return new RegExp(`\\(${esc}\\)`, 'i').test(text) || new RegExp(`\\{${esc}\\}`, 'i').test(text);
  });
}

function findUserByEmail(users, email) {
  const target = String(email || '').trim().toLowerCase();
  if (!target) return null;
  return (users || []).find((u) => String(u.email || '').trim().toLowerCase() === target) || null;
}

function buildComposeRecipients(selectedEmails, toManual) {
  const manualTok = toManual.split(/[,;\n]+/).map((s) => s.trim()).filter((s) => s.includes('@'));
  const out = [];
  const seen = new Set();
  selectedEmails.forEach((e) => {
    const x = e.toLowerCase();
    if (!seen.has(x)) {
      seen.add(x);
      out.push(e);
    }
  });
  manualTok.forEach((e) => {
    const x = e.toLowerCase();
    if (!seen.has(x)) {
      seen.add(x);
      out.push(e);
    }
  });
  return out;
}

/**
 * When Compose has exactly one To address, use that profile for template “Preview as recipient”.
 * Returns null if 0 or 2+ recipients (then the modal falls back to the demo Sean sample).
 */
function resolveMergePreviewUser(users, selectedEmails, toManual) {
  const list = buildComposeRecipients(selectedEmails, toManual);
  if (list.length !== 1) return null;
  const email = String(list[0]).trim();
  if (!email.includes('@')) return null;
  const platform = findUserByEmail(users, email);
  if (platform) return platform;
  return {
    firstName: '',
    lastName: '',
    email,
    username: email.split('@')[0] || '',
  };
}

function formatRecipientPreviewToLine(user) {
  if (!user || !user.email) return '';
  const nm = applyMergeFieldsToText('(user_full_name)', user).trim();
  if (nm) return `${nm} <${user.email}>`;
  return `<${user.email}>`;
}

function stripHtmlForSnippet(s) {
  if (s == null) return '';
  return String(s)
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function emailBodyLooksLikeHtml(s) {
  const t = String(s || '').trim();
  if (!t) return false;
  if (t.startsWith('<')) return true;
  return /<\/(p|div|br|h[1-6]|ul|ol|li)>/i.test(t) || /<img\b/i.test(t) || /<a\s+href=/i.test(t) || /<span\b/i.test(t);
}

function plainTextToTipTapHtml(text) {
  const raw = String(text ?? '');
  if (!raw.trim()) return '<p></p>';
  if (emailBodyLooksLikeHtml(raw)) return raw;
  const esc = (line) => line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return raw
    .split(/\n+/)
    .map((line) => `<p>${line ? esc(line) : '<br>'}</p>`)
    .join('');
}

function insertMergeTokenAtCursor(inputRef, value, setValue, token) {
  const insert = `(${token})`;
  const el = inputRef?.current;
  if (el && typeof el.selectionStart === 'number') {
    const start = el.selectionStart;
    const end = el.selectionEnd ?? start;
    const next = String(value).slice(0, start) + insert + String(value).slice(end);
    setValue(next);
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + insert.length;
        el.setSelectionRange(pos, pos);
      } catch {
        /* ignore */
      }
    });
  } else {
    setValue((v) => `${v}${insert}`);
  }
}

function MergeFieldInsertControl({ inputRef, value, setValue, className = '' }) {
  return (
    <select
      className={`comms-merge-field-select ${className}`.trim()}
      value=""
      aria-label="Insert merge field"
      onChange={(e) => {
        const t = e.target.value;
        if (t) insertMergeTokenAtCursor(inputRef, value, setValue, t);
        e.target.value = '';
      }}
    >
      <option value="">Insert field…</option>
      {COMPOSE_MERGE_FIELDS.map((f) => (
        <option key={f.token} value={f.token}>
          {f.label} — ({f.token})
        </option>
      ))}
    </select>
  );
}

/**
 * Pick linked documents from the same compliance index as OPO Business Auditor.
 * Nested inside Templates modal (higher z-index overlay).
 */
function ComplianceDocumentsAttachPicker({ open, onClose, onAddAttachments }) {
  const [index, setIndex] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sectionFilter, setSectionFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [pickerSel, setPickerSel] = useState(() => new Set());

  useEffect(() => {
    if (!open) return undefined;
    setPickerSel(new Set());
    setSectionFilter('all');
    setPriorityFilter('all');
    setLoadErr(null);
    setLoading(true);
    commsFetch(`${API}/api/opo/auditor/compliance-documents-index`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status === 401 ? 'Unauthorized' : 'Could not load documents'))))
      .then(setIndex)
      .catch((e) => setLoadErr(e.message || 'Load failed'))
      .finally(() => setLoading(false));
    return undefined;
  }, [open]);

  const flatItems = useMemo(() => {
    if (!index) return [];
    return [...(index.items || []), ...(index.extraDocuments || [])];
  }, [index]);

  const visibleItems = useMemo(() => {
    return flatItems
      .filter((item) => {
        if (sectionFilter !== 'all' && item.section !== sectionFilter) return false;
        if (priorityFilter === 'all') return true;
        if (priorityFilter === 'present') return item.status === 'present';
        return item.status === 'missing' && item.priority === priorityFilter;
      })
      .sort((a, b) => {
        if (a.status !== b.status) return a.status === 'missing' ? -1 : 1;
        const po = COMPLIANCE_PRIORITY_ORDER.indexOf(a.priority) - COMPLIANCE_PRIORITY_ORDER.indexOf(b.priority);
        if (po !== 0) return po;
        return (a.document || '').localeCompare(b.document || '', undefined, { sensitivity: 'base' });
      });
  }, [flatItems, sectionFilter, priorityFilter]);

  if (!open) return null;

  const toggleId = (id, attachable) => {
    if (!attachable) return;
    setPickerSel((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectAllAttachableVisible = () => {
    const n = new Set(pickerSel);
    visibleItems.forEach((it) => {
      if (it.attachable && it.linkedDocumentId != null) n.add(it.linkedDocumentId);
    });
    setPickerSel(n);
  };

  const selectWholeCategory = () => {
    if (sectionFilter === 'all') return;
    const n = new Set(pickerSel);
    flatItems.forEach((it) => {
      if (it.section === sectionFilter && it.attachable && it.linkedDocumentId != null) n.add(it.linkedDocumentId);
    });
    setPickerSel(n);
  };

  const clearPickerSelection = () => setPickerSel(new Set());

  const confirmAdd = () => {
    const byId = new Map();
    flatItems.forEach((it) => {
      if (it.linkedDocumentId != null && it.attachable) {
        byId.set(it.linkedDocumentId, it);
      }
    });
    const out = [];
    pickerSel.forEach((id) => {
      const it = byId.get(id);
      if (!it) return;
      out.push({
        linkedDocumentId: it.linkedDocumentId,
        document: it.document || it.label || 'Document',
        filePath: it.actualFile || it.filePath || '',
        section: it.section || '',
        priority: it.priority || '',
        status: it.status || '',
      });
    });
    if (out.length) onAddAttachments(out);
    onClose();
  };

  const sections = index?.sections || [];
  const summary = index?.summary;

  return (
    <div
      className="comms-attach-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="comms-attach-modal"
        role="dialog"
        aria-labelledby="comms-attach-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="comms-attach-head">
          <h2 id="comms-attach-title">Link documents</h2>
          <button type="button" className="comms-templates-close" onClick={onClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <p className="comms-attach-intro">
          Same checklist as <strong>Document compliance</strong> in OPO. Only <strong>present</strong> files registered in the linked-documents table can be attached. Missing rows are shown so you can see status.
        </p>
        {loading && <div className="comms-attach-loading"><i className="fas fa-spinner fa-spin"></i> Loading index…</div>}
        {loadErr && <div className="comms-attach-error">{loadErr}</div>}
        {!loading && !loadErr && index && (
          <>
            <div className="comms-attach-summary-bar">
              <span className="comms-attach-summary-pct">{summary.compliancePercent}%</span>
              <span className="comms-attach-summary-count">({summary.present}/{summary.total})</span>
            </div>
            <div className="comms-attach-bar-track">
              <div className="comms-attach-bar-fill" style={{ width: `${summary.compliancePercent}%` }} />
            </div>
            <div className="comms-attach-section-row">
              <button
                type="button"
                className={`comms-attach-section-btn${sectionFilter === 'all' ? ' active' : ''}`}
                onClick={() => { setSectionFilter('all'); setPriorityFilter('all'); }}
              >
                All
              </button>
              {sections.map((sec) => {
                const secItems = flatItems.filter((i) => i.section === sec);
                const secMissing = secItems.filter((i) => i.status === 'missing').length;
                return (
                  <button
                    key={sec}
                    type="button"
                    className={`comms-attach-section-btn${sectionFilter === sec ? ' active' : ''}${secMissing > 0 ? ' has-missing' : ''}`}
                    title={sec}
                    onClick={() => { setSectionFilter(sec); setPriorityFilter('all'); }}
                  >
                    {shortComplianceSectionLabel(sec)}
                    {secMissing > 0 ? <span className="comms-attach-section-badge">{secMissing}</span> : null}
                  </button>
                );
              })}
            </div>
            <div className="comms-attach-filter-row">
              {['all', 'critical', 'required', 'recommended', 'present'].map((f) => {
                const scope = sectionFilter === 'all' ? flatItems : flatItems.filter((i) => i.section === sectionFilter);
                const count = f === 'all'
                  ? scope.length
                  : f === 'present'
                    ? scope.filter((i) => i.status === 'present').length
                    : scope.filter((i) => i.priority === f && i.status === 'missing').length;
                const label = f === 'all' ? `All (${count})`
                  : f === 'present' ? `Present (${count})`
                  : f === 'critical' ? `Critical missing (${count})`
                  : f === 'required' ? `Required missing (${count})`
                  : `Recommended (${count})`;
                return (
                  <button
                    key={f}
                    type="button"
                    className={`comms-attach-filter-btn${priorityFilter === f ? ' active' : ''}`}
                    onClick={() => setPriorityFilter(f)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="comms-attach-bulk-row">
              <button type="button" className="comms-attach-bulk-btn" onClick={selectAllAttachableVisible}>
                Select all attachable (visible)
              </button>
              <button
                type="button"
                className="comms-attach-bulk-btn"
                onClick={selectWholeCategory}
                disabled={sectionFilter === 'all'}
                title={sectionFilter === 'all' ? 'Choose a category tab first' : 'Add every attachable file in this category'}
              >
                Select all attachable in category
              </button>
              <button type="button" className="comms-attach-bulk-btn" onClick={clearPickerSelection}>
                Clear selection
              </button>
            </div>
            <div className="comms-attach-list">
              {visibleItems.map((item) => {
                const id = item.linkedDocumentId;
                const canPick = item.attachable && id != null;
                const checked = canPick && pickerSel.has(id);
                return (
                  <label
                    key={item.rowKey || `${item.expectedPath}-${id}`}
                    className={`comms-attach-row comms-attach-row-${item.status}${canPick ? '' : ' comms-attach-row-disabled'}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!canPick}
                      onChange={() => toggleId(id, canPick)}
                    />
                    <span className={`comms-attach-badge comms-attach-badge-${item.priority}`}>{item.priority}</span>
                    <span className="comms-attach-doc">{item.document}</span>
                    <span className="comms-attach-owner">{item.owner}</span>
                    <span className={`comms-attach-status comms-attach-status-${item.status}`}>{item.status}</span>
                  </label>
                );
              })}
            </div>
          </>
        )}
        <div className="comms-attach-footer">
          <span className="comms-attach-sel-count">{pickerSel.size} selected</span>
          <div className="comms-attach-footer-btns">
            <button type="button" className="comms-templates-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="comms-templates-btn-primary"
              onClick={confirmAdd}
              disabled={pickerSel.size === 0}
            >
              Add to template ({pickerSel.size})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Modal: system templates with rich HTML body (TipTap); images embedded as base64 in stored HTML (SQLite). */
function ComposeTemplatesModal({
  open,
  onClose,
  templates,
  onTemplatesChange,
  onApplyToComposer,
  mergePreviewUser = null,
}) {
  const [view, setView] = useState('list');
  const [previewTpl, setPreviewTpl] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [addName, setAddName] = useState('');
  const [addSubject, setAddSubject] = useState('');
  const [addBody, setAddBody] = useState('');
  const [addAttachments, setAddAttachments] = useState([]);
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);
  const [aiEnhancing, setAiEnhancing] = useState(false);
  const [aiEnhanceError, setAiEnhanceError] = useState(null);
  const [aiEnhanceUndoStack, setAiEnhanceUndoStack] = useState([]);
  const [editorNonce, setEditorNonce] = useState(0);
  const addSubjectInputRef = useRef(null);
  const aiEnhancePollRef = useRef(null); // holds { intervalId, jobId }
  const aiEnhanceFormRef = useRef({ name: '', subject: '', body: '' });
  const [recipientPreviewOpen, setRecipientPreviewOpen] = useState(false);
  /** Original / rich HTML for iframe preview; TipTap overwrites addBody with stripped HTML on load. */
  const [richBodyForPreview, setRichBodyForPreview] = useState('');
  /** True once the user has actually typed/changed content in the TipTap editor for this session. */
  const [templateEdited, setTemplateEdited] = useState(false);
  /** 'visual' = TipTap rich editor | 'source' = raw HTML textarea (preserves full formatting) */
  const [editorMode, setEditorMode] = useState('visual');

  useEffect(() => {
    aiEnhanceFormRef.current = { name: addName, subject: addSubject, body: addBody };
  }, [addName, addSubject, addBody]);

  useEffect(() => {
    if (view !== 'form') setRecipientPreviewOpen(false);
  }, [view]);

  useEffect(() => {
    if (!open) {
      // Cancel any in-flight AI enhance poll when modal closes
      if (aiEnhancePollRef.current) {
        clearInterval(aiEnhancePollRef.current.intervalId);
        aiEnhancePollRef.current = null;
      }
      return;
    }
    setView('list');
    setPreviewTpl(null);
    setEditingId(null);
    setAddName('');
    setAddSubject('');
    setAddBody('');
    setAddAttachments([]);
    setAttachPickerOpen(false);
    setAiEnhanceError(null);
    setAiEnhancing(false);
    setAiEnhanceUndoStack([]);
    setEditorNonce(0);
    setRichBodyForPreview('');
    setTemplateEdited(false);
    setEditorMode('visual');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (attachPickerOpen) {
          e.preventDefault();
          setAttachPickerOpen(false);
        } else if (view === 'form') {
          e.preventDefault();
          /* Do not close modal or discard — use Cancel / Save on the form */
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, attachPickerOpen, view]);

  if (!open) return null;

  const effectiveMergePreviewUser =
    mergePreviewUser && mergePreviewUser.email ? mergePreviewUser : COMPOSE_PREVIEW_SAMPLE_USER;
  const previewUsesComposeRecipient = Boolean(mergePreviewUser && mergePreviewUser.email);
  const previewNameMayBeEmpty =
    previewUsesComposeRecipient &&
    !String(mergePreviewUser.firstName || '').trim() &&
    !String(mergePreviewUser.lastName || '').trim();

  const persist = (next) => {
    onTemplatesChange(next);
    persistComposeTemplatesToStorage(next);
    saveComposeTemplatesToServer(next).then((d) => {
      if (d && !d.success && typeof d.error === 'string') {
        console.warn('compose-templates save:', d.error);
      }
    });
  };

  const beginNewTemplate = () => {
    setEditingId(null);
    setAddName('');
    setAddSubject('');
    setAddBody('<p></p>');
    setRichBodyForPreview('');
    setTemplateEdited(false);
    setEditorMode('visual');
    setAddAttachments([]);
    setAiEnhanceError(null);
    setAiEnhanceUndoStack([]);
    setEditorNonce(0);
    setView('form');
  };

  const beginEditTemplate = (tpl) => {
    setEditingId(tpl.id);
    setAddName(tpl.name || '');
    setAddSubject(tpl.subject || '');
    const b = tpl.body;
    const bodyStr = b != null && String(b).trim() ? String(b) : '<p></p>';
    setAddBody(bodyStr);
    setRichBodyForPreview(bodyStr !== '<p></p>' ? bodyStr : '');
    setAddAttachments(normalizeTemplateAttachments(tpl));
    setAiEnhanceError(null);
    setAiEnhanceUndoStack([]);
    setTemplateEdited(false);
    // Auto-select Source mode for complex HTML so inline styles / gradients are never stripped by TipTap
    const isRichHtml = bodyStr.includes('linear-gradient') || bodyStr.includes('<table') || bodyStr.includes('style="background');
    setEditorMode(isRichHtml ? 'source' : 'visual');
    setEditorNonce(0);
    setView('form');
  };

  const stopAiEnhancePoll = () => {
    if (aiEnhancePollRef.current) {
      clearInterval(aiEnhancePollRef.current.intervalId);
      aiEnhancePollRef.current = null;
    }
  };

  const applyAiEnhanceResult = (result) => {
    const snap = aiEnhanceFormRef.current;
    setAiEnhanceUndoStack((prev) => {
      const next = [...prev, { name: snap.name, subject: snap.subject, body: snap.body }];
      while (next.length > AI_ENHANCE_UNDO_MAX) next.shift();
      return next;
    });
    if (result.templateName != null && String(result.templateName).trim()) setAddName(String(result.templateName).trim());
    if (result.subject != null && String(result.subject).trim()) setAddSubject(String(result.subject).trim());
    if (result.bodyHtml != null && String(result.bodyHtml).trim()) {
      const next = String(result.bodyHtml).trim();
      setAddBody(next);
      setRichBodyForPreview(next);
      setEditorNonce((n) => n + 1);
    }
    if (result.truncated) console.warn('[AI Enhance] Input was truncated; raise OLLAMA_TEMPLATE_MAX_INPUT_CHARS');
  };

  const undoAiEnhance = () => {
    if (aiEnhancing) return;
    let snapshot = null;
    setAiEnhanceUndoStack((prev) => {
      if (prev.length === 0) return prev;
      snapshot = prev[prev.length - 1];
      return prev.slice(0, -1);
    });
    if (!snapshot) return;
    setAddName(snapshot.name);
    setAddSubject(snapshot.subject);
    const restored =
      snapshot.body != null && String(snapshot.body).trim() ? String(snapshot.body) : '<p></p>';
    setAddBody(restored);
    setRichBodyForPreview(restored !== '<p></p>' ? restored : '');
    setEditorNonce((n) => n + 1);
    setAiEnhanceError(null);
  };

  const pollAiEnhanceJob = (jobId) => {
    const doOnePoll = () => {
      commsFetch(`${API}/api/communications/compose-templates/ai-enhance/${jobId}`)
        .then(async (r) => {
          const text = await r.text();
          const d = parseJsonSafe(text);
          if (!r.ok) {
            stopAiEnhancePoll();
            setAiEnhancing(false);
            setAiEnhanceError(formatAiEnhanceFailureMessage(r.status, text, d));
            return;
          }
          if (!d || !d.success) {
            stopAiEnhancePoll();
            setAiEnhancing(false);
            setAiEnhanceError(formatAiEnhanceFailureMessage(r.status, text, d));
            return;
          }
          if (d.status === 'pending') return; // still running — wait for next poll
          stopAiEnhancePoll();
          setAiEnhancing(false);
          if (d.status === 'error') {
            setAiEnhanceError(d.error || 'AI Enhance failed — check Ollama logs.');
            return;
          }
          if (d.status === 'complete' && d.result) {
            applyAiEnhanceResult(d.result);
          }
        })
        .catch((e) => {
          stopAiEnhancePoll();
          setAiEnhancing(false);
          setAiEnhanceError(e.message || String(e));
        });
    };

    doOnePoll(); // immediate first check
    const intervalId = setInterval(doOnePoll, 4000);
    aiEnhancePollRef.current = { intervalId, jobId };
  };

  const runAiEnhance = () => {
    stopAiEnhancePoll();
    setAiEnhanceError(null);
    setAiEnhancing(true);
    const bodyForAi =
      richBodyForPreview && String(richBodyForPreview).trim()
        ? richBodyForPreview
        : addBody;
    commsFetch(`${API}/api/communications/compose-templates/ai-enhance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: addName, subject: addSubject, body: bodyForAi }),
    })
      .then(async (r) => {
        const text = await r.text();
        const d = parseJsonSafe(text);
        if (!r.ok) {
          setAiEnhancing(false);
          throw new Error(formatAiEnhanceFailureMessage(r.status, text, d));
        }
        if (!d || !d.success || !d.jobId) {
          setAiEnhancing(false);
          throw new Error(formatAiEnhanceFailureMessage(r.status, text, d));
        }
        // Job accepted — begin polling
        pollAiEnhanceJob(d.jobId);
      })
      .catch((e) => {
        setAiEnhancing(false);
        setAiEnhanceError(e.message || String(e));
      });
  };

  const saveTemplateForm = () => {
    const name = addName.trim() || 'Untitled template';
    // If the user made actual TipTap edits (typed something), save their edits — the editor view
    // is the source of truth for their intent. If they opened without editing, preserve the
    // full rich HTML (richBodyForPreview) so complex layouts (tables, gradients) survive saves.
    const rich = String(richBodyForPreview || '').trim();
    const bodyToSave = (templateEdited || !rich) ? String(addBody || '') : rich;
    const row = {
      id: editingId || newComposeTemplateId(),
      name,
      subject: addSubject,
      body: bodyToSave,
      attachments: addAttachments,
      updatedAt: new Date().toISOString(),
    };
    const next = editingId ? templates.map((t) => (t.id === editingId ? row : t)) : [row, ...templates];
    persist(next);
    setView('list');
    setEditingId(null);
    setAddName('');
    setAddSubject('');
    setAddBody('');
    setAddAttachments([]);
  };

  const mergeAttachments = (list) => {
    setAddAttachments((prev) => {
      const seen = new Set(prev.map((a) => a.linkedDocumentId));
      const next = [...prev];
      for (const a of list) {
        if (a.linkedDocumentId == null || seen.has(a.linkedDocumentId)) continue;
        seen.add(a.linkedDocumentId);
        next.push(a);
      }
      return next;
    });
  };

  const removeTemplate = (id, e) => {
    e.stopPropagation();
    const next = templates.filter((t) => t.id !== id);
    persist(next);
    if (previewTpl?.id === id) {
      setPreviewTpl(null);
      setView('list');
    }
    if (editingId === id) {
      setEditingId(null);
      setView('list');
    }
  };

  const applyTemplateToComposer = (tpl) => {
    onApplyToComposer({
      subject: tpl.subject || '',
      body: tpl.body || '',
      attachments: normalizeTemplateAttachments(tpl),
      templateId: tpl.id,
    });
    onClose();
  };

  const truncate = (s, n) => {
    const t = (s || '').replace(/\s+/g, ' ').trim();
    if (t.length <= n) return t || '—';
    return `${t.slice(0, n)}…`;
  };

  return (
    <div className="comms-templates-overlay" role="presentation" aria-modal="true">
      <div
        className="comms-templates-modal"
        role="dialog"
        aria-labelledby="comms-templates-title"
      >
        <ComplianceDocumentsAttachPicker
          open={attachPickerOpen}
          onClose={() => setAttachPickerOpen(false)}
          onAddAttachments={mergeAttachments}
        />
        <div className="comms-templates-modal-head">
          <h2 id="comms-templates-title">Templates</h2>
          <button
            type="button"
            className="comms-templates-close"
            onClick={() => {
              if (view === 'form') {
                setView('list');
                setEditingId(null);
              } else {
                onClose();
              }
            }}
            aria-label={view === 'form' ? 'Leave editor (use Cancel to discard or Save to keep)' : 'Close templates'}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {view === 'list' && (
          <div className="comms-templates-body">
            <p className="comms-templates-hint">
              Templates are stored on the server (including rich text and pasted images as part of the message HTML). Use <strong>Preview</strong> or <strong>Edit</strong> on a card, then <strong>Use in composer</strong> or <strong>Apply template</strong> on the compose page.
              Merge placeholders like <code>(user_firstname)</code> or <code>{'{user_firstname}'}</code> are filled per recipient when you send (platform users).
            </p>
            <div className="comms-templates-grid">
              <button
                type="button"
                className="comms-template-add-tile"
                onClick={beginNewTemplate}
                title="Add template"
              >
                <span className="comms-template-add-plus">+</span>
                <span className="comms-template-add-label">New template</span>
              </button>
              {templates.map((tpl) => {
                const att = normalizeTemplateAttachments(tpl);
                return (
                  <div key={tpl.id} className="comms-template-card">
                    <div className="comms-template-card-actions">
                      <button
                        type="button"
                        className="comms-template-card-edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          beginEditTemplate(tpl);
                        }}
                        title="Edit template"
                        aria-label={`Edit template ${tpl.name}`}
                      >
                        <i className="fas fa-pen" />
                      </button>
                      <button
                        type="button"
                        className="comms-template-card-delete"
                        onClick={(e) => removeTemplate(tpl.id, e)}
                        title="Delete template"
                        aria-label={`Delete template ${tpl.name}`}
                      >
                        <i className="fas fa-trash-alt" />
                      </button>
                    </div>
                    <button
                      type="button"
                      className="comms-template-card-main"
                      onClick={() => {
                        setPreviewTpl(tpl);
                        setView('preview');
                      }}
                    >
                      <span className="comms-template-card-name">{tpl.name}</span>
                      <span className="comms-template-card-subject">{truncate(tpl.subject, 72)}</span>
                      <span className="comms-template-card-snippet">
                        {att.length > 0 && (
                          <span className="comms-template-card-attach-hint">
                            <i className="fas fa-paperclip"></i> {att.length} doc{att.length === 1 ? '' : 's'} ·{' '}
                          </span>
                        )}
                        {truncate(stripHtmlForSnippet(tpl.body), 140)}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
            {templates.length === 0 && (
              <p className="comms-templates-empty">No templates yet. Click the <strong>+</strong> tile to create your first one.</p>
            )}
          </div>
        )}

        {view === 'form' && (
          <div className="comms-templates-body comms-templates-add">
            <button
              type="button"
              className="comms-templates-back"
              onClick={() => {
                setView('list');
                setEditingId(null);
              }}
            >
              <i className="fas fa-arrow-left"></i> Back to list
            </button>
            <div className="comms-templates-form-title-row">
              <h3 className="comms-templates-form-title">{editingId ? 'Edit template' : 'New template'}</h3>
              <div className="comms-templates-ai-actions">
                <button
                  type="button"
                  className="comms-templates-ai-enhance-btn"
                  onClick={runAiEnhance}
                  disabled={aiEnhancing}
                  title="Reformat with Ollama — set OLLAMA_BASE_URL and OLLAMA_TEMPLATE_MODEL on the server"
                >
                  {aiEnhancing ? (
                    <>
                      <i className="fas fa-spinner fa-spin" aria-hidden /> Working… (may take 1–3 min)
                    </>
                  ) : (
                    <>
                      <i className="fas fa-wand-magic-sparkles" aria-hidden /> AI Enhance
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="comms-templates-ai-undo-btn"
                  onClick={undoAiEnhance}
                  disabled={aiEnhancing || aiEnhanceUndoStack.length === 0}
                  title="Undo last AI Enhance — restores name, subject, and message body"
                  aria-label="Undo last AI Enhance"
                >
                  <i className="fas fa-undo" aria-hidden />
                </button>
                <button
                  type="button"
                  className={`comms-templates-recipient-preview-toggle ${recipientPreviewOpen ? 'active' : ''}`}
                  onClick={() => setRecipientPreviewOpen((v) => !v)}
                  title="See merged HTML the way an inbox usually shows it (sample: Sean)"
                >
                  <i className="fas fa-envelope-open-text" aria-hidden />{' '}
                  {recipientPreviewOpen ? 'Hide recipient preview' : 'Preview as recipient'}
                </button>
              </div>
            </div>
            <label className="comms-templates-field-label">Template name</label>
            <input
              className="comms-templates-input"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder="e.g. Investor update — Q2"
            />
            <p className="comms-templates-merge-hint">
              Rich message: bold, colors, fonts, images (paste or upload — stored in the database inside this template HTML). Use <strong>Insert field</strong> in the toolbar for <code>(user_firstname)</code> etc.{' '}
              <strong>AI Enhance</strong> sends name, subject, and body to your Ollama server (model is <code>OLLAMA_TEMPLATE_MODEL</code> on the backend) and refills all three fields with email-safe HTML. Each run is saved so you can step back with the <strong>undo</strong> button (up to {AI_ENHANCE_UNDO_MAX} steps).
            </p>
            {aiEnhanceError && (
              <p className="comms-templates-ai-enhance-err" role="alert">
                <i className="fas fa-exclamation-circle" /> {aiEnhanceError}
              </p>
            )}
            <div className="comms-templates-msg-label-row">
              <label className="comms-templates-field-label">Subject</label>
              <MergeFieldInsertControl inputRef={addSubjectInputRef} value={addSubject} setValue={setAddSubject} />
            </div>
            <input
              ref={addSubjectInputRef}
              className="comms-templates-input"
              value={addSubject}
              onChange={(e) => setAddSubject(e.target.value)}
              placeholder="Email subject line"
            />
            <div className="comms-templates-msg-label-row comms-templates-msg-label-row-tools">
              <label className="comms-templates-field-label">Message</label>
              <div className="comms-templates-msg-tools">
                <button
                  type="button"
                  className="comms-templates-attach-btn"
                  onClick={() => setAttachPickerOpen(true)}
                  title="Link OptiPrime documents (compliance checklist)"
                >
                  <i className="fas fa-paperclip"></i>
                  Attach
                </button>
              </div>
            </div>
            {addAttachments.length > 0 && (
              <ul className="comms-templates-attached-chips">
                {addAttachments.map((a) => (
                  <li key={a.linkedDocumentId} className="comms-templates-attached-chip">
                    <span className="comms-templates-attached-chip-text" title={a.filePath}>{a.document}</span>
                    <button
                      type="button"
                      className="comms-templates-attached-chip-remove"
                      aria-label={`Remove ${a.document}`}
                      onClick={() => setAddAttachments((prev) => prev.filter((x) => x.linkedDocumentId !== a.linkedDocumentId))}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {/* Editor mode toggle — Source mode lets you edit raw HTML without losing rich formatting */}
            <div className="comms-templates-editor-mode-bar">
              <button
                type="button"
                className={`comms-templates-editor-mode-btn${editorMode === 'visual' ? ' active' : ''}`}
                onClick={() => setEditorMode('visual')}
                title="Visual editor (TipTap)"
              >
                <i className="fas fa-pen" /> Visual
              </button>
              <button
                type="button"
                className={`comms-templates-editor-mode-btn${editorMode === 'source' ? ' active' : ''}`}
                onClick={() => setEditorMode('source')}
                title="Edit raw HTML — preserves gradients, tables, and button styles"
              >
                <i className="fas fa-code" /> Source
              </button>
              {editorMode === 'source' && (
                <span className="comms-templates-editor-mode-hint">
                  <i className="fas fa-check-circle" style={{color:'#19efff',marginRight:4}} />
                  Source mode — full formatting preserved. Edit text directly, then click <strong>Preview as recipient</strong> to see the result.
                </span>
              )}
              {editorMode === 'visual' && richBodyForPreview && richBodyForPreview.length > (addBody || '').length + 100 && (
                <span className="comms-templates-editor-mode-hint" style={{color:'#e3a030'}}>
                  <i className="fas fa-exclamation-triangle" style={{marginRight:4}} />
                  Visual mode strips complex formatting on save. Use <strong>Source</strong> mode to edit this template safely.
                </span>
              )}
            </div>

            {editorMode === 'source' ? (
              <textarea
                className="comms-templates-source-editor"
                value={richBodyForPreview || addBody || ''}
                spellCheck={false}
                onChange={(e) => {
                  const val = e.target.value;
                  setRichBodyForPreview(val);
                  setAddBody(val);
                  setTemplateEdited(true);
                }}
              />
            ) : (
              <ComposeTemplateRichEditor
                key={`tpl-${editingId || 'new'}-${editorNonce}`}
                content={emailBodyLooksLikeHtml(addBody) ? addBody : plainTextToTipTapHtml(addBody)}
                onChange={(val) => { setAddBody(val); setTemplateEdited(true); }}
                mergeFields={COMPOSE_MERGE_FIELDS}
                placeholder="Write your message… Paste or insert images; they are saved with this template."
                className="comms-templates-rich-editor-wrap"
              />
            )}
            {recipientPreviewOpen && (
              <div className="comms-templates-recipient-preview-wrap">
                <p className="comms-templates-recipient-preview-hint">
                  {previewUsesComposeRecipient ? (
                    <>
                      <strong>Recipient preview</strong> — merge fields use your <strong>Compose “To”</strong> line:{' '}
                      <strong>{mergePreviewUser.email}</strong>. The frame matches a real inbox. For complex HTML, preview uses the full template source (TipTap may simplify the editor view).
                      {previewNameMayBeEmpty && (
                        <>
                          {' '}
                          <span className="comms-templates-recipient-preview-warn">
                            This address is not in the platform directory, so name tokens like <code>(user_firstname)</code> stay empty here — they still resolve when you send to a listed user.
                          </span>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <strong>Recipient preview</strong> — demo user <strong>Sean Investor</strong> ({COMPOSE_PREVIEW_SAMPLE_USER.email}).
                      To see <strong>real</strong> merge data, put exactly <strong>one</strong> recipient in Compose (checkbox and/or To field), then open this preview again.
                    </>
                  )}
                </p>
                <div className="comms-templates-recipient-preview-chrome">
                  <div>
                    <span className="comms-templates-recipient-preview-k">From</span>{' '}
                    <span className="comms-templates-recipient-preview-v">{OPTIPRIME_SMTP_FROM_LABEL}</span>
                  </div>
                  <div>
                    <span className="comms-templates-recipient-preview-k">To</span>{' '}
                    <span className="comms-templates-recipient-preview-v">
                      {formatRecipientPreviewToLine(effectiveMergePreviewUser)}
                    </span>
                  </div>
                  <div>
                    <span className="comms-templates-recipient-preview-k">Subject</span>{' '}
                    <span className="comms-templates-recipient-preview-v">
                      {applyMergeFieldsToText(addSubject, effectiveMergePreviewUser) || '(no subject)'}
                    </span>
                  </div>
                </div>
                <iframe
                  title="Email preview as recipient would see it"
                  className="comms-templates-email-preview-iframe"
                  srcDoc={buildEmailClientPreviewSrcDoc(
                    applyMergeFieldsToText(
                      richBodyForPreview && String(richBodyForPreview).trim()
                        ? richBodyForPreview
                        : addBody || '',
                      effectiveMergePreviewUser
                    )
                  )}
                />
              </div>
            )}
            <div className="comms-templates-actions">
              <button
                type="button"
                className="comms-templates-btn-secondary"
                onClick={() => {
                  setView('list');
                  setEditingId(null);
                }}
              >
                Cancel
              </button>
              <button type="button" className="comms-templates-btn-primary" onClick={saveTemplateForm}>
                {editingId ? 'Save changes' : 'Save template'}
              </button>
            </div>
          </div>
        )}

        {view === 'preview' && previewTpl && (
          <div className="comms-templates-body comms-templates-preview-wrap">
            <button type="button" className="comms-templates-back" onClick={() => { setPreviewTpl(null); setView('list'); }}>
              <i className="fas fa-arrow-left"></i> Back to list
            </button>
            <h3 className="comms-templates-preview-title">{previewTpl.name}</h3>
            <div className="comms-templates-preview-frame">
              <div className="comms-templates-preview-meta">
                <span>
                  <strong>From</strong> {OPTIPRIME_SMTP_FROM_LABEL}
                </span>
                <span>
                  <strong>To</strong>{' '}
                  {previewUsesComposeRecipient ? '(from Compose)' : '(demo)'}{' '}
                  {formatRecipientPreviewToLine(effectiveMergePreviewUser)}
                </span>
                <span>
                  <strong>Subject</strong>{' '}
                  {applyMergeFieldsToText(previewTpl.subject || '', effectiveMergePreviewUser) || '(no subject)'}
                </span>
              </div>
              <p className="comms-templates-preview-sample-note">
                {previewUsesComposeRecipient ? (
                  <>
                    Merge fields use your selected Compose recipient (<strong>{mergePreviewUser.email}</strong>) — same as <strong>Preview as recipient</strong> while editing.
                  </>
                ) : (
                  <>
                    Demo user <strong>Sean</strong> — put exactly one recipient in Compose to preview with real <code>(user_email)</code> / name tokens.
                  </>
                )}
              </p>
              <div className="comms-templates-preview-body comms-templates-preview-body-iframe-shell">
                {emailBodyLooksLikeHtml(previewTpl.body) ? (
                  <iframe
                    title="Template preview as email"
                    className="comms-templates-email-preview-iframe comms-templates-email-preview-iframe--in-modal"
                    srcDoc={buildEmailClientPreviewSrcDoc(
                      applyMergeFieldsToText(previewTpl.body || '', effectiveMergePreviewUser)
                    )}
                  />
                ) : (
                  <pre>{previewTpl.body || '(empty message)'}</pre>
                )}
              </div>
            </div>
            {normalizeTemplateAttachments(previewTpl).length > 0 && (
              <div className="comms-templates-preview-attachments">
                <strong>Linked documents</strong>
                <ul>
                  {normalizeTemplateAttachments(previewTpl).map((a) => (
                    <li key={a.linkedDocumentId}>
                      <a
                        href={`${API}/api/opo/auditor/linked-documents/${a.linkedDocumentId}/file`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {a.document}
                      </a>
                      <span className="comms-templates-preview-att-meta">
                        {' '}
                        · {shortComplianceSectionLabel(a.section) || a.section || '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="comms-templates-preview-note">
              Recipients are chosen in the compose form; merge fields are replaced when you send. Embedded images are stored in the template record. Linked documents open from OptiPrime (same session).
            </p>
            <div className="comms-templates-actions">
              <button type="button" className="comms-templates-btn-secondary" onClick={() => { setPreviewTpl(null); setView('list'); }}>
                Cancel
              </button>
              <button type="button" className="comms-templates-btn-secondary" onClick={() => beginEditTemplate(previewTpl)}>
                <i className="fas fa-pen" /> Edit
              </button>
              <button type="button" className="comms-templates-btn-primary" onClick={() => applyTemplateToComposer(previewTpl)}>
                <i className="fas fa-edit"></i> Use in composer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function engagementStatusForUser(u) {
  const welcomeAt = u.welcomeEmailSentAt;
  const lastLogin = u.lastLogin;
  if (welcomeAt && !lastLogin) return { code: 'no_login', label: 'NO LOGIN YET', level: 'critical' };
  if (!lastLogin) return { code: 'not_started', label: 'NOT STARTED', level: 'warning' };
  const days = (Date.now() - new Date(lastLogin).getTime()) / (24 * 60 * 60 * 1000);
  if (days > 14) return { code: 'dormant', label: 'DORMANT', level: 'warning' };
  return { code: 'active', label: 'ACTIVE', level: 'good' };
}

function isBetaEngagementUser(u) {
  if (!userEmailEligible(u)) return false;
  const email = (u.email || '').toLowerCase();
  if (email.includes('@example.com')) return false;
  if ((u.username || '').toLowerCase() === 'optiprime.system') return false;
  return true;
}

/** Per-user engagement tracking for BETA retention campaign. */
function CommunicationsEngagementView({ onViewEmails, onCompose }) {
  const [users, setUsers] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [sequenceByEmail, setSequenceByEmail] = useState({});
  const [trackingByEmail, setTrackingByEmail] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState(null);

  const loadEngagement = useCallback(() => {
    return Promise.all([
      fetch(`${API}/api/community/users`).then((r) => (r.ok ? r.json() : [])),
      commsFetch(`${API}/api/communications/sent?limit=100&page=1`).then((r) => r.json()),
      commsFetch(`${API}/api/communications/engagement-tracking`).then((r) => r.json()),
      commsFetch(`${API}/api/communications/email-sequence`).then((r) => r.json()),
    ])
      .then(([userList, sentData, trackData, seqData]) => {
        setUsers(Array.isArray(userList) ? userList.filter(isBetaEngagementUser) : []);
        setSentEmails(sentData?.success && Array.isArray(sentData.emails) ? sentData.emails : []);
        setTrackingByEmail(trackData?.success && trackData.byEmail ? trackData.byEmail : {});
        const seqMap = {};
        if (seqData?.success && Array.isArray(seqData.sequences)) {
          for (const s of seqData.sequences) seqMap[s.email] = s;
        }
        setSequenceByEmail(seqMap);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadEngagement()
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Failed to load engagement data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [loadEngagement]);

  const runBackfill = async () => {
    setBackfilling(true);
    setBackfillMsg(null);
    setError(null);
    try {
      const res = await commsFetch(`${API}/api/communications/engagement-tracking/backfill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Backfill failed');
      setTrackingByEmail(data.byEmail || {});
      setBackfillMsg(`Backfilled ${data.created} user(s)${data.skipped ? ` (${data.skipped} skipped)` : ''}.`);
      await loadEngagement();
    } catch (e) {
      setError(e.message || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  const sentByEmail = useMemo(() => {
    const map = new Map();
    for (const row of sentEmails) {
      const key = (row.toEmail || '').toLowerCase();
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    }
    return map;
  }, [sentEmails]);

  if (loading) return <div className="comms-loading">Loading engagement data…</div>;
  if (error) return <p className="comms-sent-error" role="alert">{error}</p>;

  return (
    <div className="comms-engagement-panel">
      <p className="comms-sent-intro">
        Track BETA engagement funnel per outreach email: <strong>opened</strong> (pixel) → <strong>clicked</strong> (CTA) →
        <strong>logged in</strong> (matched email). Applies to new Compose sends with tracked links.
        Use <strong>ENGAGE-01</strong> for active users; <strong>LOGIN-01/02</strong> when they have not logged in.
        {' '}
        <button
          type="button"
          className="comms-engagement-btn"
          onClick={runBackfill}
          disabled={backfilling}
          title="Infer open/click/login from sent log + user logins for outreach sent before tracking was enabled"
        >
          {backfilling ? 'Backfilling…' : 'Backfill history'}
        </button>
        {backfillMsg && <span className="comms-engagement-muted"> {backfillMsg}</span>}
      </p>
      {users.length === 0 ? (
        <div className="comms-empty">No BETA users found.</div>
      ) : (
        <div className="comms-engagement-table-wrap">
          <table className="comms-engagement-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Status</th>
                <th>Credentials email</th>
                <th>Last login</th>
                <th>Sequence</th>
                <th>Last outreach</th>
                <th>Funnel</th>
                <th>Opened</th>
                <th>Clicked</th>
                <th>Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const status = engagementStatusForUser(u);
                const sent = sentByEmail.get((u.email || '').toLowerCase()) || [];
                const lastSent = sent[0];
                const seq = sequenceByEmail[(u.email || '').toLowerCase()];
                const track = trackingByEmail[(u.email || '').toLowerCase()];
                return (
                  <tr key={u.id || u.email}>
                    <td>
                      <strong>{u.firstName} {u.lastName}</strong>
                      <br />
                      <small>{u.email}</small>
                    </td>
                    <td>
                      <span className={`comms-engagement-badge comms-engagement-${status.level}`}>
                        {status.label}
                      </span>
                    </td>
                    <td>{u.welcomeEmailSentAt ? new Date(u.welcomeEmailSentAt).toLocaleString() : '—'}</td>
                    <td>{u.lastLogin ? new Date(u.lastLogin).toLocaleString() : '—'}</td>
                    <td>
                      {seq ? (
                        <>
                          <strong>{seq.sentSequenceCount}/{seq.totalSteps}</strong>
                          <br />
                          <small>{seq.positionLabel}</small>
                        </>
                      ) : '—'}
                    </td>
                    <td>
                      {lastSent ? (
                        <>
                          {new Date(lastSent.sentAt).toLocaleDateString()}
                          <br />
                          <small>{lastSent.subject || '(no subject)'}</small>
                        </>
                      ) : '—'}
                    </td>
                    <td>
                      {track?.funnel ? (
                        <span className={`comms-engagement-badge comms-engagement-${track.funnel.level}`}>
                          {track.funnel.label}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      {track?.openedAt ? (
                        <>
                          <span className="comms-engagement-badge comms-engagement-good">YES</span>
                          {track.openCount > 1 && <small> ({track.openCount}×)</small>}
                          <br />
                          <small>{new Date(track.openedAt).toLocaleString()}</small>
                        </>
                      ) : track ? (
                        <span className="comms-engagement-badge comms-engagement-critical">NO</span>
                      ) : '—'}
                    </td>
                    <td>
                      {track?.clickedAt ? (
                        <>
                          <span className="comms-engagement-badge comms-engagement-good">YES</span>
                          <br />
                          <small>{new Date(track.clickedAt).toLocaleString()}</small>
                        </>
                      ) : '—'}
                    </td>
                    <td>
                      {track?.loginMatched && track?.loginAt ? (
                        <>
                          <span className="comms-engagement-badge comms-engagement-good">YES</span>
                          <br />
                          <small>{new Date(track.loginAt).toLocaleString()}</small>
                        </>
                      ) : track?.loginAt ? (
                        <span className="comms-engagement-badge comms-engagement-warning">Mismatch</span>
                      ) : '—'}
                    </td>
                    <td className="comms-engagement-actions">
                      <button type="button" className="comms-engagement-btn" onClick={() => onViewEmails(u.email)}>
                        View sequence
                      </button>
                      <button
                        type="button"
                        className="comms-engagement-btn comms-engagement-btn-primary"
                        onClick={() => onCompose(u.email, status.code === 'no_login' ? 'tpl_login_01' : 'tpl_engage_01')}
                      >
                        Compose
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function sequenceStepBadgeClass(status) {
  if (status === 'sent') return 'good';
  if (status === 'due') return 'warning';
  if (status === 'waiting') return 'warning';
  if (status === 'skipped') return 'muted';
  if (status === 'blocked') return 'muted';
  return 'critical';
}

function sequenceStepLabel(status) {
  const map = {
    sent: 'Sent',
    due: 'Due now',
    waiting: 'In wait cycle',
    skipped: 'Skipped',
    blocked: 'Blocked',
    pending: 'Not sent',
  };
  return map[status] || status;
}

/** BETA email sequence monitor — planned steps, wait cycles, and off-sequence sends. */
function CommunicationsSequenceView({ toFilter = '', onCompose, onSelectUser }) {
  const [sequences, setSequences] = useState([]);
  const [sequenceDef, setSequenceDef] = useState([]);
  const [activeEmail, setActiveEmail] = useState(toFilter.trim().toLowerCase());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSentId, setSelectedSentId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const loadSequences = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (activeEmail && activeEmail.includes('@')) params.set('email', activeEmail);
      const res = await commsFetch(`${API}/api/communications/email-sequence?${params}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not load sequence');
      setSequences(data.sequences || []);
      setSequenceDef(data.sequenceDefinition || []);
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [activeEmail]);

  useEffect(() => {
    setActiveEmail(toFilter.trim().toLowerCase());
  }, [toFilter]);

  useEffect(() => {
    void loadSequences();
  }, [loadSequences]);

  const activeSequence = useMemo(() => {
    if (!activeEmail) return sequences[0] || null;
    return sequences.find((s) => s.email === activeEmail) || sequences[0] || null;
  }, [sequences, activeEmail]);

  const fetchDetail = (id) => {
    setSelectedSentId(id);
    setDetail(null);
    setDetailLoading(true);
    commsFetch(`${API}/api/communications/sent/${id}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.email) setDetail(data.email);
        else setDetail(null);
      })
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  const removeOffSequence = async (id) => {
    if (!window.confirm('Remove this test/duplicate send from the log?')) return;
    setRemovingId(id);
    try {
      const res = await commsFetch(`${API}/api/communications/sent/${id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Delete failed');
      if (selectedSentId === id) {
        setSelectedSentId(null);
        setDetail(null);
      }
      await loadSequences();
    } catch (e) {
      setError(e.message || 'Could not remove row');
    } finally {
      setRemovingId(null);
    }
  };

  const pickUser = (email) => {
    const e = String(email || '').trim().toLowerCase();
    setActiveEmail(e);
    setSelectedSentId(null);
    setDetail(null);
    if (onSelectUser) onSelectUser(e);
  };

  return (
    <div className="comms-seq-panel">
      <p className="comms-sent-intro">
        Monitor the <strong>BETA email sequence</strong> for each user: credentials → ENGAGE-01 → LOGIN-01 → LOGIN-02.
        Date-triggered steps show <strong>wait cycle</strong> progress. Off-sequence / test sends can be removed.
      </p>
      <div className="comms-seq-toolbar">
        <div className="comms-seq-user-picker">
          <label htmlFor="comms-seq-user-select">User</label>
          <select
            id="comms-seq-user-select"
            value={activeEmail || ''}
            onChange={(e) => pickUser(e.target.value)}
          >
            <option value="">All BETA users</option>
            {sequences.map((s) => (
              <option key={s.email} value={s.email}>
                {s.name} ({s.email})
              </option>
            ))}
          </select>
        </div>
        <button type="button" className="comms-sent-refresh" onClick={() => void loadSequences()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {error && <p className="comms-sent-error" role="alert">{error}</p>}

      {!activeEmail && (
        <div className="comms-seq-overview-wrap">
          {loading && sequences.length === 0 && <div className="comms-loading">Loading sequences…</div>}
          {!loading && sequences.length === 0 && <div className="comms-empty">No BETA users found.</div>}
          <table className="comms-engagement-table comms-seq-overview-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Sequence position</th>
                <th>Steps sent</th>
                <th>Next step</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sequences.map((s) => (
                <tr key={s.email}>
                  <td>
                    <strong>{s.name}</strong>
                    <br />
                    <small>{s.email}</small>
                  </td>
                  <td>{s.positionLabel}</td>
                  <td>{s.sentSequenceCount} / {s.totalSteps}</td>
                  <td>
                    {s.nextStep ? (
                      <span className="comms-engagement-badge comms-engagement-warning">{s.nextStep.code}</span>
                    ) : (
                      <span className="comms-engagement-badge comms-engagement-good">—</span>
                    )}
                  </td>
                  <td className="comms-engagement-actions">
                    <button type="button" className="comms-engagement-btn" onClick={() => pickUser(s.email)}>
                      Open sequence
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeEmail && activeSequence && (
        <div className="comms-seq-layout">
          <div className="comms-seq-main">
            <div className="comms-seq-user-head">
              <div>
                <h3 className="comms-seq-user-title">{activeSequence.name}</h3>
                <p className="comms-seq-user-meta">{activeSequence.email}</p>
              </div>
              <div className="comms-seq-user-status">
                <span className={`comms-engagement-badge comms-engagement-${activeSequence.hasLogin ? 'good' : 'warning'}`}>
                  {activeSequence.hasLogin ? 'LOGGED IN' : 'NO LOGIN YET'}
                </span>
                <span className="comms-seq-position">{activeSequence.positionLabel}</span>
              </div>
            </div>

            {activeSequence.nextStep && !activeSequence.hasLogin && (
              <div className="comms-seq-next-card">
                <strong>Next in sequence:</strong> {activeSequence.nextStep.code} — {activeSequence.nextStep.label}
                {onCompose && activeSequence.nextStep.templateId && (
                  <button
                    type="button"
                    className="comms-engagement-btn comms-engagement-btn-primary"
                    onClick={() => onCompose(activeSequence.email, activeSequence.nextStep.templateId)}
                  >
                    Compose {activeSequence.nextStep.code}
                  </button>
                )}
              </div>
            )}

            <ol className="comms-seq-timeline">
              {activeSequence.steps.map((step) => (
                <li key={step.stepId} className={`comms-seq-step comms-seq-step-${step.status}`}>
                  <div className="comms-seq-step-head">
                    <span className="comms-seq-step-order">{step.order}</span>
                    <div className="comms-seq-step-titles">
                      <strong>{step.code}</strong>
                      <span>{step.label}</span>
                    </div>
                    <span className={`comms-engagement-badge comms-engagement-${sequenceStepBadgeClass(step.status)}`}>
                      {sequenceStepLabel(step.status)}
                    </span>
                  </div>
                  {step.sentAt && (
                    <p className="comms-seq-step-meta">
                      Sent {new Date(step.sentAt).toLocaleString()}
                      {step.subject && <> · <em>{step.subject}</em></>}
                    </p>
                  )}
                  {step.status === 'waiting' && step.wait && (
                    <div className="comms-seq-wait">
                      <div className="comms-seq-wait-bar" aria-hidden="true">
                        <span style={{ width: `${step.wait.progressPct}%` }} />
                      </div>
                      <p className="comms-seq-wait-label">
                        Wait cycle: day {step.wait.daysElapsed} of {step.wait.waitDays}
                        {step.wait.daysRemaining > 0 && <> · {step.wait.daysRemaining} day(s) until due</>}
                      </p>
                    </div>
                  )}
                  <div className="comms-seq-step-actions">
                    {step.sentId && (
                      <button type="button" className="comms-engagement-btn" onClick={() => fetchDetail(step.sentId)}>
                        View message
                      </button>
                    )}
                    {step.status === 'due' && onCompose && step.templateId && (
                      <button
                        type="button"
                        className="comms-engagement-btn comms-engagement-btn-primary"
                        onClick={() => onCompose(activeSequence.email, step.templateId)}
                      >
                        Send {step.code}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ol>

            {activeSequence.offSequence.length > 0 && (
              <div className="comms-seq-offsequence">
                <h4>Off-sequence / test sends ({activeSequence.offSequence.length})</h4>
                <ul className="comms-seq-offsequence-list">
                  {activeSequence.offSequence.map((row) => (
                    <li key={row.id} className="comms-seq-offsequence-row">
                      <div>
                        <strong>{row.kind === 'duplicate_test' ? 'Duplicate test' : 'Manual send'}</strong>
                        {row.duplicateOf && <> (dup of {row.duplicateOf})</>}
                        <br />
                        <small>{row.subject || '(no subject)'}</small>
                        <br />
                        <small>{row.sentAt ? new Date(row.sentAt).toLocaleString() : '—'}</small>
                      </div>
                      <div className="comms-seq-offsequence-actions">
                        <button type="button" className="comms-engagement-btn" onClick={() => fetchDetail(row.id)}>
                          View
                        </button>
                        <button
                          type="button"
                          className="comms-engagement-btn"
                          onClick={() => void removeOffSequence(row.id)}
                          disabled={removingId === row.id}
                        >
                          {removingId === row.id ? 'Removing…' : 'Remove'}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="comms-sent-detail-wrap comms-seq-detail-wrap">
            {!selectedSentId && (
              <p className="comms-sent-detail-placeholder">
                Select a sent step or off-sequence row to preview the message.
              </p>
            )}
            {selectedSentId && detailLoading && <div className="comms-loading">Loading message…</div>}
            {selectedSentId && !detailLoading && detail && (
              <div className="comms-sent-detail">
                <h4 className="comms-sent-detail-title">Message</h4>
                <dl className="comms-sent-detail-dl">
                  <dt>To</dt>
                  <dd>{detail.toEmail}</dd>
                  <dt>Sent</dt>
                  <dd>{formatSyncTimestamp(detail.sentAt)}</dd>
                  <dt>Subject</dt>
                  <dd>{detail.subject || '(no subject)'}</dd>
                </dl>
                <div className="comms-sent-detail-body-label">Body</div>
                {emailBodyLooksLikeHtml(detail.body) ? (
                  <div className="comms-sent-detail-body-html" dangerouslySetInnerHTML={{ __html: detail.body }} />
                ) : (
                  <pre className="comms-sent-detail-body-plain">{detail.body || '—'}</pre>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeEmail && !activeSequence && !loading && (
        <div className="comms-empty">No sequence data for this user.</div>
      )}

      {!activeEmail && sequenceDef.length > 0 && (
        <div className="comms-seq-legend">
          <strong>Planned sequence:</strong>
          {' '}
          {sequenceDef.map((s) => `${s.code} (${s.label}${s.waitDaysAfterPrior ? `, +${s.waitDaysAfterPrior}d wait` : ''})`).join(' → ')}
        </div>
      )}
    </div>
  );
}

export default function CommunicationsDashboard({ sidebarCollapsed = false }) {
  const [activeTab, setActiveTab] = useState('optiprime');
  const [optiprimeSub, setOptiprimeSub] = useState('compose');
  const [sentToFilter, setSentToFilter] = useState('');
  const [composeInitialTo, setComposeInitialTo] = useState('');
  const [composeInitialTemplateId, setComposeInitialTemplateId] = useState('');
  const [gmailSelectedIds, setGmailSelectedIds] = useState(() => new Set());
  const [gmailListRefreshTick, setGmailListRefreshTick] = useState(0);
  const bumpGmailInboxRefresh = useCallback(() => setGmailListRefreshTick((t) => t + 1), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const compose = params.get('compose');
    const to = (params.get('to') || '').trim();
    const template = (params.get('template') || '').trim();
    const sentTo = (params.get('sent_to') || '').trim();
    if (compose === '1' || to || template) {
      setActiveTab('optiprime');
      setOptiprimeSub('compose');
      if (to) setComposeInitialTo(to);
      if (template) setComposeInitialTemplateId(template);
    }
    if (sentTo) {
      setActiveTab('optiprime');
      setOptiprimeSub('sent');
      setSentToFilter(sentTo);
    }
    if (params.get('inbox') === '1') {
      setActiveTab('optiprime');
      setOptiprimeSub('inbox');
    }
    if (params.get('outbound') === '1' || params.get('tab') === 'outbound') {
      setActiveTab('outbound');
    }
    if (compose || to || template || sentTo || params.get('inbox') === '1' || params.get('outbound') === '1' || params.get('tab') === 'outbound') {
      const clean = window.location.pathname;
      window.history.replaceState({}, '', clean);
    }
  }, []);

  const openComposeForUser = useCallback((email, templateId) => {
    setActiveTab('optiprime');
    setOptiprimeSub('compose');
    setComposeInitialTo(email || '');
    setComposeInitialTemplateId(templateId || '');
    setSentToFilter('');
  }, []);

  const openSentForUser = useCallback((email) => {
    setActiveTab('optiprime');
    setOptiprimeSub('sent');
    setSentToFilter(email || '');
  }, []);

  // ─── Tactical hero + KPI ──────────────────────────────────────────────
  const [stats, setStats] = useState(null);
  const [statsTick, setStatsTick] = useState(0);
  const [heroSyncing, setHeroSyncing] = useState(false);
  const [heroMessage, setHeroMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await commsFetch(`${API}/api/communications/stats?${String(Date.now())}`);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (data && data.success && data.stats) setStats(data.stats);
      } catch {
        /* keep last stats */
      }
    };
    void load();
    const interval = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [statsTick]);

  const triggerHeroSync = useCallback(async () => {
    if (heroSyncing) return;
    setHeroSyncing(true);
    setHeroMessage(null);
    try {
      const res = await commsFetch(`${API}/api/communications/sync`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Sync failed');
      setHeroMessage('Gmail sync completed.');
    } catch (e) {
      setHeroMessage(e.message || 'Sync request failed.');
    } finally {
      setHeroSyncing(false);
      setStatsTick((n) => n + 1);
    }
  }, [heroSyncing]);

  const level = deriveCommsLevel(stats);
  const levelLabel = levelLabelForState(level);
  const totalEmails = stats?.totalEmails ?? 0;
  const pending = stats?.pendingInstructions ?? 0;
  const pendingLevel = stats ? levelFromPendingInstructions(pending) : 'unknown';
  const lastCycleIso = stats?.lastSyncSessionAt || null;
  const newestIso = stats?.newestMirroredEmailAt || null;
  const lastCycleAgeMin = lastCycleIso ? (Date.now() - new Date(lastCycleIso).getTime()) / 60000 : null;
  const newestAgeMin = newestIso ? (Date.now() - new Date(newestIso).getTime()) / 60000 : null;
  const lastCycleLevel = lastCycleIso ? levelFromMinutes(lastCycleAgeMin, 15, 30) : 'unknown';
  // Mailbox volume rises and falls — “fresh enough” is anything inside ~24h.
  const newestLevel = newestIso ? levelFromMinutes(newestAgeMin, 360, 1440) : 'unknown';

  const heroSubBits = [];
  heroSubBits.push(`${totalEmails.toLocaleString()} mirrored`);
  heroSubBits.push(`last cycle ${formatRelativeTime(lastCycleIso)}`);
  heroSubBits.push(`${pending} pending instruction${pending === 1 ? '' : 's'}`);
  heroSubBits.push(`status: ${stats?.syncStatus || 'unknown'}`);

  const tabDefs = [
    { id: 'optiprime', label: 'OPTIPRIME', icon: 'fas fa-building' },
    { id: 'gmail', label: 'GMAIL', icon: 'fab fa-google' },
    { id: 'outbound', label: 'OUTBOUND', icon: 'fas fa-paper-plane' },
    { id: 'summary', label: 'DAILY SUMMARY', icon: 'fas fa-newspaper' },
    { id: 'sync', label: 'SYNC', icon: 'fas fa-sync-alt' },
  ];

  return (
    <div className="communications-dashboard comms-shell opo-tactical">
      {/* ─── Hero status banner ───────────────────────────────────────── */}
      <div className={`opo-hero opo-hero-${level}`}>
        <div className="opo-hero-scan" aria-hidden="true" />
        <div className="opo-hero-left">
          <div className="opo-hero-eyebrow">COMMUNICATIONS RELAY · GMAIL · OPTIPRIME</div>
          <div className="opo-hero-title">
            <span className={`opo-hero-pulse opo-pulse-${level}`} />
            <h2>{levelLabel}</h2>
          </div>
          <div className="opo-hero-sub">{heroSubBits.join(' · ')}</div>
          {heroMessage && (
            <div className="comms-hero-flash" role="status">{heroMessage}</div>
          )}
        </div>
        <div className="opo-hero-right">
          <button
            type="button"
            className="opo-refresh-btn"
            onClick={() => void triggerHeroSync()}
            title="Run Gmail sync now"
            disabled={heroSyncing}
          >
            <span className="opo-refresh-icon" /> {heroSyncing ? 'SYNCING…' : 'SYNC'}
          </button>
          <div className="opo-hero-timestamp">
            {lastCycleIso ? new Date(lastCycleIso).toLocaleTimeString() : '—'}
          </div>
        </div>
      </div>

      {/* ─── KPI strip ────────────────────────────────────────────────── */}
      <div className="opo-kpi-row comms-kpi-row">
        <div className="opo-kpi">
          <div className="opo-kpi-label">EMAILS MIRRORED</div>
          <div className="opo-kpi-value opo-mono-value">{totalEmails.toLocaleString()}</div>
          <div className="opo-kpi-foot">total in <code>email_inbox</code></div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">PENDING INSTRUCTIONS</div>
          <div className={`opo-kpi-value opo-mono-value opo-level-${pendingLevel}`}>
            {pending.toLocaleString()}
          </div>
          <div className="opo-kpi-foot">queue · agent runs ~5m</div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">LAST SYNC CYCLE</div>
          <div className={`opo-kpi-value opo-mono-value opo-level-${lastCycleLevel}`}>
            {formatRelativeTime(lastCycleIso)}
          </div>
          <div className="opo-kpi-foot">{lastCycleIso ? new Date(lastCycleIso).toLocaleTimeString() : '—'}</div>
        </div>
        <div className="opo-kpi">
          <div className="opo-kpi-label">NEWEST EMAIL</div>
          <div className={`opo-kpi-value opo-mono-value opo-level-${newestLevel}`}>
            {formatRelativeTime(newestIso)}
          </div>
          <div className="opo-kpi-foot">latest <code>received_at</code></div>
        </div>
      </div>

      {/* ─── Tab strip (tactical) ─────────────────────────────────────── */}
      <div className="comms-tabs comms-tabs-primary comms-tactical-tabs" role="tablist" aria-label="Communications channels">
        {tabDefs.map((t) => {
          const isActive = activeTab === t.id;
          let pipLevel = 'unknown';
          if (stats) {
            if (t.id === 'sync') pipLevel = level;
            else if (t.id === 'gmail') pipLevel = lastCycleLevel;
            else if (t.id === 'outbound') pipLevel = 'good';
            else if (t.id === 'summary') pipLevel = pendingLevel;
            else pipLevel = 'good';
          }
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={isActive ? 'active' : ''}
              onClick={() => setActiveTab(t.id)}
            >
              <span className={`opo-pip opo-pip-${pipLevel}`} aria-hidden="true">
                <span className="opo-pip-core" />
              </span>
              <i className={t.icon} aria-hidden="true"></i>
              <span className="comms-tab-label">{t.label}</span>
              {t.id === 'summary' && pending > 0 && (
                <span className="comms-tab-counter" title="Pending instructions">{pending}</span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === 'optiprime' && (
        <div className="comms-optiprime-panel comms-tactical-panel">
          <div className="comms-tactical-panel-head">
            <span className="comms-tactical-panel-title">
              <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
              {optiprimeSub === 'inbox'
                ? 'INBOUND CHANNEL · OPTIPRIME IMAP'
                : 'OUTBOUND CHANNEL · OPTIPRIME SMTP'}
            </span>
            <span className="comms-tactical-panel-tag">CONTACT@OPTIPRIME.AI</span>
          </div>
          <div className="comms-email-subtabs comms-optiprime-subtabs comms-tactical-subtabs" role="tablist" aria-label="OptiPrime mail">
            <button
              type="button"
              role="tab"
              aria-selected={optiprimeSub === 'compose'}
              className={optiprimeSub === 'compose' ? 'active' : ''}
              onClick={() => setOptiprimeSub('compose')}
            >
              <i className="fas fa-pen" aria-hidden="true"></i> COMPOSE
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={optiprimeSub === 'inbox'}
              className={optiprimeSub === 'inbox' ? 'active' : ''}
              onClick={() => setOptiprimeSub('inbox')}
            >
              <i className="fas fa-inbox" aria-hidden="true"></i> INBOX
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={optiprimeSub === 'engagement'}
              className={optiprimeSub === 'engagement' ? 'active' : ''}
              onClick={() => setOptiprimeSub('engagement')}
            >
              <i className="fas fa-users" aria-hidden="true"></i> ENGAGEMENT
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={optiprimeSub === 'sent'}
              className={optiprimeSub === 'sent' ? 'active' : ''}
              onClick={() => { setOptiprimeSub('sent'); }}
            >
              <i className="fas fa-stream" aria-hidden="true"></i> SEQUENCE
            </button>
          </div>
          {optiprimeSub === 'compose' && (
            <OptiPrimeSendView
              initialTo={composeInitialTo}
              initialTemplateId={composeInitialTemplateId}
              onInitialApplied={() => {
                setComposeInitialTo('');
                setComposeInitialTemplateId('');
              }}
            />
          )}
          {optiprimeSub === 'inbox' && (
            <OptiPrimeInboxView onComposeToUser={openComposeForUser} />
          )}
          {optiprimeSub === 'engagement' && (
            <CommunicationsEngagementView
              onViewEmails={openSentForUser}
              onCompose={openComposeForUser}
            />
          )}
          {optiprimeSub === 'sent' && (
            <CommunicationsSequenceView
              toFilter={sentToFilter}
              onCompose={openComposeForUser}
              onSelectUser={setSentToFilter}
            />
          )}
        </div>
      )}

      {activeTab === 'outbound' && (
        <div className="comms-tactical-panel">
          <div className="comms-tactical-panel-head">
            <span className="comms-tactical-panel-title">
              <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
              OUTBOUND GENERATORS · TWO MAILBOXES
            </span>
            <span className="comms-tactical-panel-tag">
              OPTIPRIMEBC@GMAIL.COM · CONTACT@OPTIPRIME.AI
            </span>
          </div>
          <CommunicationsOutboundView />
        </div>
      )}

      {activeTab === 'gmail' && (
        <div className="comms-email-panel comms-gmail-panel comms-tactical-panel">
          <div className="comms-tactical-panel-head">
            <span className="comms-tactical-panel-title">
              <span className={`opo-pip opo-pip-${lastCycleLevel}`}><span className="opo-pip-core" /></span>
              INBOUND CHANNEL · GMAIL MIRROR
            </span>
            <span className="comms-tactical-panel-tag">
              {totalEmails.toLocaleString()} MIRRORED · CYCLE {formatRelativeTime(lastCycleIso).toUpperCase()}
            </span>
          </div>
          <EmailBrowserView
            selectedIds={gmailSelectedIds}
            setSelectedIds={setGmailSelectedIds}
            listRefreshTick={gmailListRefreshTick}
            onRefresh={bumpGmailInboxRefresh}
            sidebarCollapsed={sidebarCollapsed}
          />
          <div className="comms-gmail-coding-spacer" aria-hidden="true" />
          <GmailAgentCodingBar variant="floating" sidebarCollapsed={sidebarCollapsed} />
        </div>
      )}

      {activeTab === 'summary' && (
        <div className="comms-tactical-panel">
          <div className="comms-tactical-panel-head">
            <span className="comms-tactical-panel-title">
              <span className={`opo-pip opo-pip-${pendingLevel}`}><span className="opo-pip-core" /></span>
              DAILY SUMMARY · AI BRIEFING
            </span>
            <span className="comms-tactical-panel-tag">
              {pending} PENDING · NEWEST {formatRelativeTime(newestIso).toUpperCase()}
            </span>
          </div>
          <DailySummaryView />
        </div>
      )}

      {activeTab === 'sync' && (
        <div className="comms-tactical-panel">
          <div className="comms-tactical-panel-head">
            <span className="comms-tactical-panel-title">
              <span className={`opo-pip opo-pip-${level}`}><span className="opo-pip-core" /></span>
              SYNC TELEMETRY · GMAIL MONITOR
            </span>
            <span className="comms-tactical-panel-tag">
              STATUS · {(stats?.syncStatus || 'UNKNOWN').toUpperCase()}
            </span>
          </div>
          <SyncStatusView />
        </div>
      )}
    </div>
  );
}

// Split summary into sections at ## and ### headings only (not ####+).
// This keeps sub-content (####) inside its parent section rather than creating empty stubs.
function getSummarySections(summaryText) {
  if (!summaryText || typeof summaryText !== 'string') return [];
  const normalized = summaryText.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  // Strip leading/trailing --- separator lines before splitting
  const cleaned = normalized.replace(/^---\n+/, '').replace(/\n+---$/, '').trim();
  if (!cleaned) return [];
  // Only split at h2 (##) and h3 (###) — not h4+ so content stays with its parent section
  const parts = cleaned.split(/\n(?=#{2,3} )/).filter((s) => s.trim());
  return parts.length > 0 ? parts : [cleaned];
}

// Renders one summary's sections + feedback. Used for pinned latest and for each history item.
function SummarySectionsBlock({ summary, summaryDate, sectionFeedback, setSectionFeedback, sendInstruction, sending, instructions = [], prefixKey = '' }) {
  const rawText = summary?.summary_text != null ? String(summary.summary_text) : '';
  const sections = rawText ? getSummarySections(rawText) : [];
  const blocks = sections.length > 0 ? sections : (rawText ? [rawText] : []);
  return (
    <div className="summary-sections">
      {blocks.map((block, i) => {
        const key = `${prefixKey}s${i}`;
        const title = block.split('\n')[0].replace(/^#+\s*/, '');
        const sectionInstructions = instructions.filter((inst) => {
          let ctx = inst.context_data;
          if (typeof ctx === 'string') {
            try { ctx = JSON.parse(ctx); } catch { ctx = {}; }
          }
          ctx = ctx || {};
          const instSection = ctx.summary_section != null ? String(ctx.summary_section).trim() : '';
          const instDate = ctx.summary_date != null ? String(ctx.summary_date).trim() : '';
          const matchSection = instSection === (title || '').trim();
          const matchDate = instDate === (summaryDate || '').trim();
          return matchSection && matchDate;
        });
        const body = (block && block.trim()) || '';
        return (
          <div key={key} className="summary-section">
            <details open={i < 3}>
              <summary>{title || 'Section'}</summary>
              <div className="summary-section-body">
                <div className="summary-markdown">
                  {body ? <ReactMarkdown>{body}</ReactMarkdown> : <p className="comms-empty">No content for this section.</p>}
                </div>
                {sectionInstructions.length > 0 && (
                  <div className="section-events">
                    <div className="section-events-label">Record of events</div>
                    {sectionInstructions.map((inst) => {
                      const dt = inst.created_at ? new Date(inst.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';
                      const statusLabel = inst.status === 'completed' ? 'Completed' : inst.status === 'failed' ? 'Failed' : 'Sent';
                      return (
                        <blockquote key={inst.id} className="section-event">
                          <span className="section-event-meta">[{dt}]</span>{' '}
                          &ldquo;{inst.instruction_text}&rdquo;{' '}
                          <span className={`section-event-status status-${inst.status}`}>[{statusLabel}]</span>
                        </blockquote>
                      );
                    })}
                  </div>
                )}
                <div className="section-feedback">
                  <input
                    type="text"
                    placeholder="Instruction for this section (e.g. Archive these emails)"
                    value={sectionFeedback[key] || ''}
                    onChange={(e) => setSectionFeedback((prev) => ({ ...prev, [key]: e.target.value }))}
                  />
                  <button
                    onClick={() => sendInstruction(key, { summary_section: title, summary_date: summaryDate })}
                    disabled={!sectionFeedback[key]?.trim() || sending === key}
                  >
                    {sending === key ? 'Sending...' : 'Send to agent'}
                  </button>
                </div>
              </div>
            </details>
          </div>
        );
      })}
    </div>
  );
}

function DailySummaryView() {
  const [pinnedSummary, setPinnedSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instructions, setInstructions] = useState([]);
  const [sectionFeedback, setSectionFeedback] = useState({});
  const [sending, setSending] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/communications/summary/latest`).then((r) => r.json()),
      fetch(`${API}/api/communications/summary/history`).then((r) => r.json())
    ])
      .then(([latestRes, historyRes]) => {
        if (latestRes.success) setPinnedSummary(latestRes.summary);
        if (historyRes.success) setHistory(historyRes.history || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const [lastInstructionsFetch, setLastInstructionsFetch] = useState(null);
  const [lastProcessedAt, setLastProcessedAt] = useState(null);

  const fetchInstructions = () => {
    fetch(`${API}/api/communications/instructions?limit=20`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setInstructions(data.instructions || []);
          setLastInstructionsFetch(new Date());
          if (data.last_processed_at != null) setLastProcessedAt(data.last_processed_at);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchAll();
    fetchInstructions();
  }, []);

  // Poll instructions while any are pending so the UI updates when the agent finishes
  const hasPending = instructions.some((i) => i.status === 'pending');
  useEffect(() => {
    if (!hasPending) return;
    const interval = setInterval(fetchInstructions, 30000); // every 30s
    return () => clearInterval(interval);
  }, [hasPending]);

  const sendInstruction = (sectionKey, contextData) => {
    const text = sectionFeedback[sectionKey]?.trim();
    if (!text) return;
    setSending(sectionKey);
    fetch(`${API}/api/communications/instructions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        instruction: text,
        contextType: contextData ? 'summary_section' : 'general',
        contextData: contextData || {}
      })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setSectionFeedback((prev) => ({ ...prev, [sectionKey]: '' }));
          fetchInstructions();
        }
        setSending(null);
      })
      .catch(() => setSending(null));
  };

  const retryInstruction = (id) => {
    if (!id || retryingId) return;
    setRetryingId(id);
    fetch(`${API}/api/communications/instructions/${id}/retry`, { method: 'POST' })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) fetchInstructions();
      })
      .finally(() => setRetryingId(null));
  };

  const pinnedDate = pinnedSummary?.summary_date || '';
  const historyExcludingPinned = history.filter((h) => h.summary_date !== pinnedDate);

  return (
    <div className="daily-summary-view">
      <div className="summary-toolbar">
        <span className="summary-toolbar-label">Latest summary is pinned at the top; manage emails from there or from history below.</span>
        <button onClick={() => { setLoading(true); fetchAll(); fetchInstructions(); }}>Refresh</button>
      </div>
      {loading && <div className="comms-loading">Loading...</div>}
      {!loading && (
        <>
          {/* Pinned — latest summary at top */}
          <div className="summary-pinned">
            <h3 className="summary-pinned-title">
              <i className="fas fa-thumbtack"></i> Latest summary
            </h3>
            {!pinnedSummary && <div className="comms-empty">No summary yet. Summaries appear here after the Gmail Monitor runs.</div>}
            {pinnedSummary && (
              <div className="summary-content">
                <div className="summary-meta">
                  {pinnedSummary.summary_date} · Last updated: {pinnedSummary.last_updated || pinnedSummary.summary_date} · Emails: {pinnedSummary.emails_processed} · Status: {pinnedSummary.status}
                </div>
                <SummarySectionsBlock
                  summary={pinnedSummary}
                  summaryDate={pinnedDate}
                  sectionFeedback={sectionFeedback}
                  setSectionFeedback={setSectionFeedback}
                  sendInstruction={sendInstruction}
                  sending={sending}
                  instructions={instructions}
                  prefixKey="pin-"
                />
              </div>
            )}
          </div>

          {/* History — past summaries below */}
          <div className="summary-history">
            <h3 className="summary-history-title">Summary history</h3>
            {historyExcludingPinned.length === 0 && !pinnedSummary && <div className="comms-empty comms-empty-sub">No past summaries.</div>}
            {historyExcludingPinned.length === 0 && pinnedSummary && <div className="comms-empty comms-empty-sub">No older summaries.</div>}
            {historyExcludingPinned.length > 0 && (
              <ul className="summary-history-list">
                {historyExcludingPinned.map((row) => (
                  <li key={row.summary_date} className="summary-history-item">
                    <details open>
                      <summary className="summary-history-item-head">
                        <span className="summary-history-date">{row.summary_date}</span>
                        <span className="summary-history-meta">Emails: {row.emails_processed} · {row.last_updated || row.status}</span>
                      </summary>
                      <div className="summary-content summary-content-history">
                        <div className="summary-meta">{row.summary_date} · {row.last_updated} · Status: {row.status}</div>
                        <SummarySectionsBlock
                          summary={row}
                          summaryDate={row.summary_date}
                          sectionFeedback={sectionFeedback}
                          setSectionFeedback={setSectionFeedback}
                          sendInstruction={sendInstruction}
                          sending={sending}
                          instructions={instructions}
                          prefixKey={`hist-${row.summary_date}-`}
                        />
                      </div>
                    </details>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="instructions-history">
            <h3>Recent instructions</h3>
            <p className="instructions-status-line">
              List last updated: {lastInstructionsFetch ? lastInstructionsFetch.toLocaleTimeString() : '—'}
              {lastProcessedAt != null && (
                <> · Agent last processed an instruction: {new Date(lastProcessedAt).toLocaleString()}</>
              )}
            </p>
            {hasPending && (
              <p className="instructions-poll-note">Checking every 30s — status will update when the agent finishes (runs about every 5 min).</p>
            )}
            <ul>
              {instructions.slice(0, 10).map((inst) => (
                <li key={inst.id} className="inst-row">
                  <span className="inst-text">{inst.instruction_text}</span>
                  <span className={`inst-status ${inst.status}`}>{inst.status}</span>
                  {inst.result && <span className="inst-result">{inst.result}</span>}
                  {inst.status === 'failed' && (
                    <button
                      type="button"
                      className="inst-retry"
                      onClick={() => retryInstruction(inst.id)}
                      disabled={retryingId === inst.id}
                      title="Queue this instruction for the agent to try again"
                    >
                      {retryingId === inst.id ? 'Retrying…' : 'Retry'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

function EmailBrowserView({ selectedIds, setSelectedIds, listRefreshTick = 0, onRefresh, sidebarCollapsed = false }) {
  const [emails, setEmails] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [labels, setLabels] = useState([]);
  /** Gmail label *id* for filtering (matches JSON in email_inbox.gmail_labels); empty = all labels */
  const [selectedLabelId, setSelectedLabelId] = useState('');
  /** System folder (INBOX, SENT, …) — exclusive with custom `selectedLabelId` for API `label_id` */
  const [systemFolderFilter, setSystemFolderFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [emailDetail, setEmailDetail] = useState(null);
  const [instructionText, setInstructionText] = useState('');
  const [sending, setSending] = useState(false);
  const [agentError, setAgentError] = useState(null);
  const [agentSummary, setAgentSummary] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [previewLines, setPreviewLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingInbox, setSyncingInbox] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [directBusy, setDirectBusy] = useState(false);
  const [directMsg, setDirectMsg] = useState(null);
  const [quickLabelApply, setQuickLabelApply] = useState('');
  const [quickLabelRemove, setQuickLabelRemove] = useState('');
  const [quickReplyBody, setQuickReplyBody] = useState('');
  const bulkFloatRef = useRef(null);
  /** After drag: pixel position; null = use default anchored layout */
  const [bulkFloatPos, setBulkFloatPos] = useState(null);
  const [bulkFloatDrag, setBulkFloatDrag] = useState(null);

  useEffect(() => {
    if (selectedIds.size === 0) {
      setBulkFloatPos(null);
      setBulkFloatDrag(null);
    }
  }, [selectedIds.size]);

  useEffect(() => {
    if (!bulkFloatDrag) return;
    const onMove = (e) => {
      const el = bulkFloatRef.current;
      if (!el) return;
      const dx = e.clientX - bulkFloatDrag.sx;
      const dy = e.clientY - bulkFloatDrag.sy;
      let nl = bulkFloatDrag.ol + dx;
      let nt = bulkFloatDrag.ot + dy;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const pad = 8;
      nl = Math.max(pad, Math.min(nl, window.innerWidth - w - pad));
      nt = Math.max(pad, Math.min(nt, window.innerHeight - h - pad));
      setBulkFloatPos({ left: nl, top: nt });
    };
    const onUp = () => setBulkFloatDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [bulkFloatDrag]);

  const onBulkFloatDragStart = (e) => {
    if (e.button !== 0) return;
    const el = bulkFloatRef.current;
    if (!el) return;
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    const ol = bulkFloatPos != null ? bulkFloatPos.left : rect.left;
    const ot = bulkFloatPos != null ? bulkFloatPos.top : rect.top;
    if (bulkFloatPos == null) {
      setBulkFloatPos({ left: rect.left, top: rect.top });
    }
    setBulkFloatDrag({ sx: e.clientX, sy: e.clientY, ol, ot });
  };

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    setRefreshError(null);
    const params = new URLSearchParams({
      page: String(pagination.page),
      limit: String(pagination.limit),
      _: String(Date.now()),
    });
    const labelIdForApi = systemFolderFilter || selectedLabelId;
    if (labelIdForApi) params.set('label_id', labelIdForApi);
    if (search) params.set('search', search);
    try {
      const res = await commsFetch(`${API}/api/communications/emails?${params}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      if (data.success) {
        setEmails(data.emails || []);
        setPagination((prev) => ({ ...prev, ...data.pagination }));
      } else {
        setRefreshError(data.error || 'Could not load emails');
      }
    } catch (e) {
      setRefreshError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, systemFolderFilter, selectedLabelId, search]);

  const syncFromGmailThenReload = useCallback(async () => {
    setSyncingInbox(true);
    setRefreshError(null);
    try {
      const res = await commsFetch(`${API}/api/communications/sync`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Gmail sync failed');
      fetchLabels();
      await fetchEmails();
    } catch (e) {
      setRefreshError(e.message || 'Sync failed');
    } finally {
      setSyncingInbox(false);
    }
  }, [fetchEmails]);

  const fetchLabels = () => {
    commsFetch(`${API}/api/communications/labels`)
      .then((res) => res.json())
      .then((data) => data.success && setLabels(data.labels || []))
      .catch(() => {});
  };

  useEffect(() => {
    fetchLabels();
  }, []);
  useEffect(() => {
    void fetchEmails();
  }, [fetchEmails, listRefreshTick]);

  const quickLabelTiles = useMemo(() => {
    return GMAIL_QUICK_LABEL_PRESETS.map((preset) => {
      const found = (labels || []).find((l) => preset.match(String(l.label_name || '')));
      const labelId = found ? String(found.label_id || found.label_name || '').trim() : '';
      return {
        tileTitle: preset.tileTitle,
        label_id: labelId,
        gmailName: found?.label_name || null,
      };
    });
  }, [labels]);

  const closeReadModal = useCallback(() => {
    setEmailDetail(null);
    setSelectedId(null);
  }, []);

  useEffect(() => {
    if (!emailDetail) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeReadModal();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [emailDetail, closeReadModal]);

  const fetchDetail = (id) => {
    setSelectedId(id);
    commsFetch(`${API}/api/communications/emails/${id}`)
      .then((res) => res.json())
      .then((data) => data.success && setEmailDetail(data.email))
      .catch(() => setEmailDetail(null));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runAgentExecute = useCallback(async (body) => {
    const base = `${API}/api/opo/communications/agent-execute`;
    const payload = { ...body };
    if (!body.confirmed) {
      payload.async = true;
    }
    const res = await commsFetch(base, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (res.status === 202) {
      const started = await res.json().catch(() => ({}));
      const jobId = started.jobId;
      if (!jobId) throw new Error(started.error || 'No job id from server');

      const deadline = Date.now() + 20 * 60 * 1000;
      const pollUrl = `${API}/api/opo/communications/agent-execute/jobs/${jobId}`;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 1500));
        const pr = await commsFetch(pollUrl);
        const j = await pr.json().catch(() => ({}));
        if (j.status === 'pending') continue;
        if (j.status === 'failed') {
          throw new Error(j.error || 'Gmail agent job failed');
        }
        if (j.status === 'completed' && j.result) {
          const sc = j.statusCode != null ? j.statusCode : 200;
          if (sc >= 400) {
            throw new Error(j.result.error || `Request failed (${sc})`);
          }
          return j.result;
        }
      }
      throw new Error('Timed out waiting for the Gmail agent (Ollama planning took too long).');
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, []);

  const submitGmailAgent = async (confirmed = false, plan = null) => {
    const instr = instructionText.trim();
    if (!instr || selectedIds.size === 0 || sending) return;
    setSending(true);
    setAgentError(null);
    setAgentSummary(null);
    try {
      const payload = {
        instruction: instr,
        email_ids: Array.from(selectedIds),
        agent: 'gmail',
        confirmed: confirmed === true,
      };
      if (confirmed && plan) payload.plan = plan;
      const data = await runAgentExecute(payload);

      if (data.clarification && (!data.actions || data.actions.length === 0)) {
        setAgentSummary({ type: 'info', text: data.clarification });
        setConfirmOpen(false);
        setPendingPlan(null);
        return;
      }

      if (data.confirmation_required && data.plan) {
        const lines = (data.preview || data.plan).map((step, i) => {
          const sub = step.subject ? ` — ${step.subject}` : '';
          return `${i + 1}. ${step.action}${sub}${step.reason ? ` (${step.reason})` : ''}`;
        });
        setPreviewLines(lines);
        setPendingPlan(data.plan);
        setConfirmOpen(true);
        return;
      }

      setConfirmOpen(false);
      setPendingPlan(null);

      if (data.results && data.results.length) {
        const ok = data.results.filter((r) => r.ok).length;
        const fail = data.results.length - ok;
        const parts = [];
        if (ok) parts.push(`${ok} step(s) ok`);
        if (fail) parts.push(`${fail} failed`);
        setAgentSummary({
          type: data.success ? 'success' : 'warn',
          text: parts.join(' · '),
          detail: data.results,
        });
        if (data.success) {
          setInstructionText('');
          setSelectedIds(new Set());
        }
      } else if (data.success === false) {
        setAgentError(data.error || 'Execute failed');
      } else {
        setAgentSummary({ type: 'success', text: 'Done.' });
        setInstructionText('');
        setSelectedIds(new Set());
      }

      if (data.refreshNeeded && typeof onRefresh === 'function') {
        onRefresh();
      }
    } catch (e) {
      setAgentError(e.message || 'Request failed');
    } finally {
      setSending(false);
    }
  };

  const confirmAgentExecute = () => {
    if (!pendingPlan) return;
    setConfirmOpen(false);
    void submitGmailAgent(true, pendingPlan);
  };

  const cancelAgentConfirm = () => {
    setConfirmOpen(false);
    setPendingPlan(null);
  };

  const postOpoExecute = async (action, params, confirmDestructive) => {
    const res = await commsFetch(`${API}/api/opo/communications/execute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action,
        params,
        confirm: confirmDestructive === true,
        agent: 'gmail',
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      throw new Error(data.error || `Request failed (${res.status})`);
    }
    return data;
  };

  const refreshAfterDirectAction = async () => {
    if (typeof onRefresh === 'function') {
      onRefresh();
    }
    await fetchEmails();
  };

  /** Run same Gmail /execute action for each selected mirror row id (no LLM). */
  const runDirectOnSelected = async (label, action, buildParams, confirmDestructive) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || directBusy) return;
    setDirectBusy(true);
    setDirectMsg(null);
    try {
      const failed = [];
      for (const emailId of ids) {
        try {
          const params = buildParams(emailId);
          await postOpoExecute(action, params, confirmDestructive);
        } catch (e) {
          failed.push({ emailId, error: e.message });
        }
      }
      if (failed.length) {
        setDirectMsg({
          ok: false,
          text:
            failed.length === ids.length
              ? `${label} failed: ${failed.map((f) => f.error).join('; ')}`
              : `${label}: ${ids.length - failed.length} ok, ${failed.length} failed (${failed.map((f) => `#${f.emailId}`).join(', ')})`,
        });
      } else {
        setDirectMsg({ ok: true, text: `${label}: ${ids.length} message(s).` });
        setSelectedIds(new Set());
        if (selectedId != null && ids.includes(selectedId)) {
          setEmailDetail(null);
          setSelectedId(null);
        }
      }
      await refreshAfterDirectAction();
    } catch (e) {
      setDirectMsg({ ok: false, text: e.message || 'Request failed' });
    } finally {
      setDirectBusy(false);
    }
  };

  const quickArchive = () =>
    void runDirectOnSelected('Archive', 'archive', (emailId) => ({ emailId }), false);

  const quickStar = () => void runDirectOnSelected('Star', 'star', (emailId) => ({ emailId }), false);

  const quickUnstar = () => void runDirectOnSelected('Unstar', 'unstar', (emailId) => ({ emailId }), false);

  const quickTrash = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const msg =
      ids.length === 1
        ? 'Move this message to Gmail Trash?'
        : `Move ${ids.length} messages to Gmail Trash?`;
    if (!window.confirm(msg)) return;
    void runDirectOnSelected('Trash', 'trash', (emailId) => ({ emailId }), true);
  };

  const quickApplyLabel = () => {
    const name = String(quickLabelApply || '').trim();
    if (!name) {
      setDirectMsg({ ok: false, text: 'Choose a label to apply.' });
      return;
    }
    void runDirectOnSelected(
      `Label “${name}”`,
      'label',
      (emailId) => ({ emailId, labelName: name, removeFromInbox: true }),
      false
    );
  };

  const quickRemoveLabel = () => {
    const name = String(quickLabelRemove || '').trim();
    if (!name) {
      setDirectMsg({ ok: false, text: 'Choose a label to remove.' });
      return;
    }
    void runDirectOnSelected(
      `Remove label “${name}”`,
      'remove_label',
      (emailId) => ({ emailId, labelName: name }),
      false
    );
  };

  const quickReply = async () => {
    if (selectedIds.size !== 1) {
      setDirectMsg({ ok: false, text: 'Reply works with exactly one selected message.' });
      return;
    }
    const body = quickReplyBody.trim();
    if (!body) {
      setDirectMsg({ ok: false, text: 'Enter reply text.' });
      return;
    }
    const emailId = Array.from(selectedIds)[0];
    setDirectBusy(true);
    setDirectMsg(null);
    try {
      await postOpoExecute('reply', { emailId, body }, false);
      setDirectMsg({ ok: true, text: 'Reply sent.' });
      setQuickReplyBody('');
      setSelectedIds(new Set());
      await refreshAfterDirectAction();
    } catch (e) {
      setDirectMsg({ ok: false, text: e.message || 'Reply failed' });
    } finally {
      setDirectBusy(false);
    }
  };

  return (
    <div
      className={`email-browser-view${selectedIds.size > 0 ? ' email-browser-view--selection-active' : ''}`}
    >
      <div className="comms-gmail-system-folders" role="toolbar" aria-label="Gmail system folders">
        {GMAIL_SYSTEM_FOLDER_FILTERS.map(({ id, title, tone }) => (
          <button
            key={id}
            type="button"
            className={`comms-gmail-folder-btn comms-gmail-folder-btn--${tone} ${systemFolderFilter === id ? 'active' : ''}`}
            title={title}
            aria-pressed={systemFolderFilter === id}
            onClick={() => {
              setSystemFolderFilter((prev) => (prev === id ? '' : id));
              setSelectedLabelId('');
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          >
            <span className="comms-gmail-folder-btn-text">{id}</span>
          </button>
        ))}
      </div>
      <div
        className="comms-gmail-quick-labels"
        role="toolbar"
        aria-label="Quick filters by Gmail user label"
      >
        {quickLabelTiles.map(({ tileTitle, label_id: lid, gmailName }, idx) => {
          const active = Boolean(lid) && selectedLabelId === lid;
          return (
            <button
              key={`quick-${idx}-${tileTitle}`}
              type="button"
              disabled={!lid}
              className={`comms-gmail-folder-btn comms-gmail-folder-btn--quick ${active ? 'active' : ''}`}
              title={
                gmailName
                  ? `Filter: ${gmailName} (${lid})`
                  : 'Label not found in mirror — sync Gmail or check label name in Gmail'
              }
              aria-pressed={active}
              onClick={() => {
                if (!lid) return;
                setSystemFolderFilter('');
                setSelectedLabelId((prev) => (prev === lid ? '' : lid));
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            >
              <span className="comms-gmail-folder-btn-text comms-gmail-folder-btn-text--quick">{tileTitle}</span>
            </button>
          );
        })}
      </div>
      <div className="browser-toolbar">
        <select
          value={selectedLabelId}
          onChange={(e) => {
            setSelectedLabelId(e.target.value);
            setSystemFolderFilter('');
            setPagination((p) => ({ ...p, page: 1 }));
          }}
          aria-label="Filter by Gmail label"
        >
          <option value="">All labels</option>
          {labels.map((l) => (
            <option key={l.label_id} value={l.label_id || l.label_name}>
              {l.label_name} ({l.count})
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination((p) => ({ ...p, page: 1 }));
          }}
        />
        <div className="browser-toolbar-actions">
          <button
            type="button"
            className="btn-reload-inbox"
            onClick={() => void fetchEmails()}
            disabled={loading || syncingInbox}
          >
            {loading && !syncingInbox ? 'Loading…' : 'Reload list'}
          </button>
          <button
            type="button"
            className="btn-sync-gmail"
            onClick={() => void syncFromGmailThenReload()}
            disabled={loading || syncingInbox}
            title="Runs one Gmail sync (incremental or full), then reloads this list from the database"
          >
            {syncingInbox ? 'Syncing Gmail…' : 'Pull from Gmail'}
          </button>
        </div>
      </div>
      {refreshError && <p className="comms-error" role="alert">{refreshError}</p>}
      <p className="comms-toolbar-hint">Reload list reads the local mirror. Use Pull from Gmail to fetch new mail from Google first (can take up to a minute).</p>
      <p className="comms-deploy-build-stamp subtle" title="Matches console: Deployment Version — rebuild admin client and redeploy if this does not change after a release.">
        Deployment build:{' '}
        <code>{import.meta.env.REACT_APP_DEPLOYMENT_VERSION || (typeof window !== 'undefined' ? window.DEPLOYMENT_VERSION : '')}</code>
      </p>
      {selectedIds.size > 0 && (
        <div
          ref={bulkFloatRef}
          className="bulk-instruction bulk-instruction-floating"
          style={
            bulkFloatPos
              ? {
                  left: bulkFloatPos.left,
                  top: bulkFloatPos.top,
                  right: 'auto',
                  bottom: 'auto',
                  width: 'min(560px, calc(100vw - 24px))',
                }
              : {
                  left: sidebarCollapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
                }
          }
        >
          <div className="bulk-quick-actions">
            <div
              className="bulk-quick-actions-title-row bulk-instruction-drag-handle"
              onMouseDown={onBulkFloatDragStart}
              role="group"
              aria-label="Drag to move panel"
            >
              <div className="bulk-quick-actions-title">
                <i className="fas fa-grip-vertical bulk-instruction-drag-icon" aria-hidden="true"></i>
                Gmail actions (direct API, no AI)
              </div>
              <span className="bulk-instruction-floating-count">{selectedIds.size} selected</span>
            </div>
            <p className="bulk-quick-actions-hint">
              Same as <code>/api/opo/communications/execute</code> — trash, archive, labels, star, reply (one message only).
            </p>
            <div className="bulk-quick-actions-row">
              <button
                type="button"
                className="bulk-quick-btn"
                onClick={() => void quickArchive()}
                disabled={directBusy || sending || syncingInbox}
              >
                Archive
              </button>
              <button
                type="button"
                className="bulk-quick-btn"
                onClick={() => void quickStar()}
                disabled={directBusy || sending || syncingInbox}
              >
                Star
              </button>
              <button
                type="button"
                className="bulk-quick-btn"
                onClick={() => void quickUnstar()}
                disabled={directBusy || sending || syncingInbox}
              >
                Unstar
              </button>
              <button
                type="button"
                className="bulk-quick-btn bulk-quick-btn-danger"
                onClick={() => void quickTrash()}
                disabled={directBusy || sending || syncingInbox}
              >
                Trash
              </button>
              <button
                type="button"
                className="bulk-quick-btn bulk-quick-btn-sync"
                onClick={() => void syncFromGmailThenReload()}
                disabled={directBusy || sending || syncingInbox}
                title="Fetch latest from Google, then reload this list"
              >
                {syncingInbox ? 'Syncing…' : 'Sync from Gmail'}
              </button>
            </div>
            <div className="bulk-quick-actions-row bulk-quick-label-row">
              <label className="bulk-quick-label">
                Apply label
                <select
                  value={quickLabelApply}
                  onChange={(e) => setQuickLabelApply(e.target.value)}
                  disabled={directBusy || sending}
                >
                  <option value="">Select…</option>
                  {labels.map((l) => (
                    <option key={l.label_id} value={l.label_name}>
                      {l.label_name}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="bulk-quick-btn"
                onClick={() => void quickApplyLabel()}
                disabled={directBusy || sending || !quickLabelApply}
              >
                Apply
              </button>
              <label className="bulk-quick-label">
                Remove label
                <select
                  value={quickLabelRemove}
                  onChange={(e) => setQuickLabelRemove(e.target.value)}
                  disabled={directBusy || sending}
                >
                  <option value="">Select…</option>
                  {labels.map((l) => (
                    <option key={`rm-${l.label_id}`} value={l.label_name}>
                      {l.label_name}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="bulk-quick-btn"
                onClick={() => void quickRemoveLabel()}
                disabled={directBusy || sending || !quickLabelRemove}
              >
                Remove
              </button>
            </div>
            {selectedIds.size === 1 && (
              <div className="bulk-quick-reply">
                <label className="bulk-quick-reply-label">Reply to selected message</label>
                <div className="bulk-quick-reply-row">
                  <textarea
                    className="bulk-quick-reply-textarea"
                    placeholder="Reply body…"
                    value={quickReplyBody}
                    onChange={(e) => setQuickReplyBody(e.target.value)}
                    disabled={directBusy || sending}
                    rows={2}
                  />
                  <button
                    type="button"
                    className="bulk-quick-btn bulk-quick-btn-primary"
                    onClick={() => void quickReply()}
                    disabled={directBusy || sending || !quickReplyBody.trim()}
                  >
                    Send reply
                  </button>
                </div>
              </div>
            )}
            {directMsg && (
              <p
                className={directMsg.ok ? 'bulk-direct-msg ok' : 'bulk-direct-msg err'}
                role="status"
              >
                {directMsg.text}
              </p>
            )}
          </div>
          <hr className="bulk-instruction-sep" />
          <p className="bulk-instruction-hint">
            Natural-language agent (below) uses Ollama — not the 5‑min queue. Destructive plans ask for confirmation first.
          </p>
          {agentError && <p className="bulk-instruction-error" role="alert">{agentError}</p>}
          {agentSummary && (
            <div
              className={
                agentSummary.type === 'success' ? 'bulk-instruction-summary ok' : 'bulk-instruction-summary'
              }
            >
              {agentSummary.text}
              {agentSummary.detail && (
                <ul className="bulk-instruction-result-list">
                  {agentSummary.detail.map((r, i) => (
                    <li key={i}>
                      {r.action} #{r.email_id}: {r.ok ? 'ok' : r.error || 'failed'}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="bulk-instruction-row">
            <textarea
              className="bulk-instruction-textarea"
              placeholder='e.g. Archive these, trash them, or reply saying thanks'
              value={instructionText}
              onChange={(e) => setInstructionText(e.target.value)}
              disabled={sending}
              rows={2}
            />
            <div className="bulk-instruction-actions">
              <button
                type="button"
                className="bulk-instruction-execute"
                onClick={() => void submitGmailAgent(false)}
                disabled={sending || !instructionText.trim()}
              >
                {sending ? 'Running…' : 'Execute'}
              </button>
              <button type="button" onClick={() => setSelectedIds(new Set())} disabled={sending}>
                Clear selection
              </button>
            </div>
          </div>
          {confirmOpen && (
            <div className="bulk-agent-modal-backdrop" role="presentation" onClick={cancelAgentConfirm}>
              <div
                className="bulk-agent-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="bulk-agent-confirm-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 id="bulk-agent-confirm-title">Confirm actions</h3>
                <p className="bulk-instruction-hint">The agent will run the following on your Gmail account:</p>
                <ul className="bulk-agent-confirm-list">
                  {previewLines.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
                <div className="bulk-agent-modal-actions">
                  <button type="button" onClick={cancelAgentConfirm} disabled={sending}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bulk-agent-confirm-danger"
                    onClick={() => void confirmAgentExecute()}
                    disabled={sending}
                  >
                    {sending ? 'Running…' : 'Confirm & run'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {loading && <div className="comms-loading">Loading emails...</div>}
      {!loading && (
        <div className="browser-list">
          {emails.map((e) => (
            <div
              key={e.id}
              className={`browser-email-row ${selectedId === e.id ? 'selected' : ''} ${selectedIds.has(e.id) ? 'checked' : ''}`}
              onClick={() => fetchDetail(e.id)}
            >
              <input type="checkbox" checked={selectedIds.has(e.id)} onChange={() => toggleSelect(e.id)} onClick={(ev) => ev.stopPropagation()} />
              <span className="from">{e.sender_email}</span>
              <span className="subject">{e.subject}</span>
              <span className="date">{e.received_at ? new Date(e.received_at).toLocaleDateString() : ''}</span>
            </div>
          ))}
        </div>
      )}
      {pagination.totalPages > 1 && (
        <div className="browser-pagination">
          <button type="button" disabled={pagination.page <= 1} onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}>Prev</button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <button type="button" disabled={pagination.page >= pagination.totalPages} onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}>Next</button>
        </div>
      )}
      {emailDetail && (
        <div
          className="comms-read-modal-backdrop"
          role="presentation"
          onClick={closeReadModal}
        >
          <div
            className="comms-read-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="comms-read-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="comms-read-modal-header">
              <h2 id="comms-read-modal-title" className="comms-read-modal-title">
                {emailDetail.subject || '(no subject)'}
              </h2>
              <button
                type="button"
                className="comms-read-modal-close"
                onClick={closeReadModal}
                aria-label="Close message"
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div className="comms-read-modal-meta">
              <span>
                <strong>From</strong> {emailDetail.sender_email || '—'}
              </span>
              {emailDetail.recipient_email && (
                <span>
                  <strong>To</strong> {emailDetail.recipient_email}
                </span>
              )}
              <span>
                <strong>Date</strong>{' '}
                {emailDetail.received_at
                  ? new Date(emailDetail.received_at).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })
                  : '—'}
              </span>
            </div>
            <div className="comms-read-modal-body">
              {emailDetail.body_text?.trim() ? (
                <pre className="comms-read-modal-pre">{emailDetail.body_text}</pre>
              ) : String(emailDetail.body_html || '').trim() ? (
                <div
                  className="comms-read-modal-html"
                  dangerouslySetInnerHTML={{ __html: String(emailDetail.body_html) }}
                />
              ) : (
                <pre className="comms-read-modal-pre">(no body)</pre>
              )}
            </div>
            <div className="comms-read-modal-footer">
              <button type="button" className="comms-read-modal-done" onClick={closeReadModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Send tab: compose and send a new email
function EmailSendView() {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = () => {
    if (!to.trim()) return;
    setSending(true);
    setResult(null);
    fetch(`${API}/api/communications/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to: to.trim(), subject: subject.trim(), body: body.trim() })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setTo('');
          setSubject('');
          setBody('');
          setResult({ ok: true, message: data.message || 'Email sent.' });
        } else {
          setResult({ ok: false, message: data.error || 'Send failed.' });
        }
        setSending(false);
      })
      .catch((err) => {
        setResult({ ok: false, message: err.message || 'Request failed.' });
        setSending(false);
      });
  };

  return (
    <div className="email-send-view">
      <div className="send-form">
        <label>To</label>
        <input type="email" placeholder="recipient@example.com" value={to} onChange={(e) => setTo(e.target.value)} />
        <label>Subject</label>
        <input type="text" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <label>Body</label>
        <textarea placeholder="Message" rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
        <button onClick={send} disabled={sending || !to.trim()}>{sending ? 'Sending...' : 'Send'}</button>
      </div>
      {result && <p className={result.ok ? 'send-success' : 'send-error'}>{result.message}</p>}
    </div>
  );
}

// Reply tab: choose an email from list, then compose reply
function EmailReplyView() {
  const [emails, setEmails] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchEmails = () => {
    setLoading(true);
    const params = new URLSearchParams({ page: pagination.page, limit: pagination.limit });
    fetch(`${API}/api/communications/emails?${params}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setEmails(data.emails || []);
          setPagination((prev) => ({ ...prev, ...data.pagination }));
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchEmails(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const sendReply = () => {
    if (!selectedEmail || !replyBody.trim()) return;
    setSending(true);
    setResult(null);
    fetch(`${API}/api/communications/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailId: selectedEmail.id, body: replyBody.trim() })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setReplyBody('');
          setSelectedEmail(null);
          setResult({ ok: true, message: data.message || 'Reply sent.' });
        } else {
          setResult({ ok: false, message: data.error || 'Reply failed.' });
        }
        setSending(false);
      })
      .catch((err) => {
        setResult({ ok: false, message: err.message || 'Request failed.' });
        setSending(false);
      });
  };

  return (
    <div className="email-reply-view">
      <div className="reply-layout">
        <div className="reply-list">
          <h4>Select an email to reply to</h4>
          {loading && <div className="comms-loading">Loading...</div>}
          {!loading && emails.map((e) => (
            <div
              key={e.id}
              className={`reply-email-row ${selectedEmail?.id === e.id ? 'selected' : ''}`}
              onClick={() => { setSelectedEmail(e); setResult(null); }}
            >
              <span className="from">{e.sender_email}</span>
              <span className="subject">{e.subject}</span>
            </div>
          ))}
        </div>
        <div className="reply-compose">
          {selectedEmail ? (
            <>
              <p><strong>Replying to:</strong> {selectedEmail.sender_email} — {selectedEmail.subject}</p>
              <textarea placeholder="Your reply" rows={6} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} />
              <button onClick={sendReply} disabled={sending || !replyBody.trim()}>{sending ? 'Sending...' : 'Send reply'}</button>
              <button type="button" className="secondary" onClick={() => { setSelectedEmail(null); setReplyBody(''); setResult(null); }}>Clear</button>
            </>
          ) : (
            <p className="comms-empty">Select an email from the list to reply.</p>
          )}
        </div>
      </div>
      {result && <p className={result.ok ? 'send-success' : 'send-error'}>{result.message}</p>}
    </div>
  );
}

const OPTIPRIME_INBOX_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'beta', label: 'BETA users' },
  { id: 'password_request', label: 'Password requests' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'social', label: 'Social' },
  { id: 'newsletter', label: 'Newsletters' },
  { id: 'other', label: 'Other' },
  { id: 'archived', label: 'Archived' },
];

function inboxCategoryLabel(category) {
  const map = {
    beta: 'BETA',
    password_request: 'Password',
    linkedin: 'LinkedIn',
    social: 'Social',
    newsletter: 'Newsletter',
    sent: 'Sent',
    other: 'Other',
  };
  return map[category] || category || 'Other';
}

function suggestedReplyForEmail(email) {
  if (!email) return '';
  if (email.automationHint === 'password_resend') {
    return `Hi${email.betaUser?.firstName ? ` ${email.betaUser.firstName}` : ''},

Thanks for reaching out — we will resend your OptiPrime login details shortly.

Your username is: ${email.betaUser?.username || '(see prior welcome email)'}
Portal: https://app.optiprime.ai

If you still have trouble logging in, reply here and we will help you personally.

— The OptiPrime Team`;
  }
  if (email.isBetaUser) {
    return `Hi${email.betaUser?.firstName ? ` ${email.betaUser.firstName}` : ''},

Thank you for your message. We received your note and a member of the OptiPrime team will follow up shortly.

— The OptiPrime Team`;
  }
  return '';
}

/** Porkbun INBOX + Sent mirror — sync, filter, read, reply (automation-ready). */
function OptiPrimeInboxView({ onComposeToUser }) {
  const [emails, setEmails] = useState([]);
  const [counts, setCounts] = useState({ inbox: {}, sent: 0 });
  const [mailbox, setMailbox] = useState('inbox');
  const [filter, setFilter] = useState('beta');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 40, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);
  const [stats, setStats] = useState(null);
  const [configured, setConfigured] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [replyResult, setReplyResult] = useState(null);
  const initialSyncDone = useRef(false);

  const fetchCounts = useCallback(() => {
    commsFetch(`${API}/api/communications/optiprime/inbox-counts`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setCounts(d.counts || {}); })
      .catch(() => {});
  }, []);

  const fetchStats = useCallback(() => {
    fetch(`${API}/api/communications/optiprime/stats`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setStats(d.stats); })
      .catch(() => {});
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(pagination.page),
      limit: String(pagination.limit),
      filter: mailbox === 'sent' ? 'all' : filter,
      mailbox,
    });
    if (search.trim()) params.set('search', search.trim());
    try {
      const res = await commsFetch(`${API}/api/communications/optiprime/emails?${params}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not load inbox');
      setEmails(data.emails || []);
      setPagination((p) => ({ ...p, ...data.pagination }));
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, filter, mailbox]);

  const runSync = useCallback(async (silent = false) => {
    if (syncing) return;
    setSyncing(true);
    if (!silent) setSyncMessage(null);
    try {
      const res = await commsFetch(`${API}/api/communications/optiprime/sync`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Sync failed');
      const parts = [data.message || 'Sync completed.'];
      if (data.newBetaReplies > 0) parts.push(`${data.newBetaReplies} new BETA message(s) logged.`);
      setSyncMessage(parts.join(' '));
      fetchStats();
      fetchCounts();
      await fetchList();
    } catch (e) {
      setSyncMessage(e.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }, [syncing, fetchList, fetchStats, fetchCounts]);

  useEffect(() => {
    fetch(`${API}/api/communications/optiprime/config`)
      .then((r) => r.json())
      .then((d) => setConfigured(d.success ? d.configured : false))
      .catch(() => setConfigured(false));
    fetchStats();
    fetchCounts();
  }, [fetchStats, fetchCounts]);

  useEffect(() => {
    if (configured === false) {
      setLoading(false);
      return;
    }
    if (configured !== true) return;
    void fetchList();
  }, [configured, fetchList]);

  useEffect(() => {
    if (configured !== true || initialSyncDone.current) return;
    initialSyncDone.current = true;
    void runSync(true);
  }, [configured, runSync]);

  const fetchDetail = (id) => {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    setReplyResult(null);
    commsFetch(`${API}/api/communications/optiprime/emails/${id}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.email) {
          setDetail(data.email);
          setReplyBody(suggestedReplyForEmail(data.email));
        } else {
          setDetail(null);
        }
      })
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  const sendReply = () => {
    if (!selectedId || !replyBody.trim()) return;
    setSending(true);
    setReplyResult(null);
    commsFetch(`${API}/api/communications/optiprime/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailId: selectedId, body: replyBody.trim() }),
    })
      .then((r) => r.json())
      .then((d) => {
        setReplyResult(d.success ? { ok: true, message: d.message } : { ok: false, message: d.error });
        if (d.success) setReplyBody('');
      })
      .catch((e) => setReplyResult({ ok: false, message: e.message }))
      .finally(() => setSending(false));
  };

  const archiveSelected = () => {
    if (!selectedId) return;
    commsFetch(`${API}/api/communications/optiprime/emails/${selectedId}/archive`, { method: 'POST' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setSelectedId(null);
          setDetail(null);
          fetchCounts();
          fetchList();
        }
      })
      .catch(() => {});
  };

  if (configured === false) {
    return (
      <div className="optiprime-email-view">
        <p className="optiprime-account-note">
          Configure <strong>contact@optiprime.ai</strong> by setting <code>OPTIPRIME_EMAIL_PASSWORD</code> in the backend environment.
        </p>
      </div>
    );
  }

  return (
    <div className="comms-inbox-panel">
      <p className="comms-sent-intro">
        Mirrored from Porkbun <strong>INBOX</strong> and <strong>Sent</strong> (full mailbox on each sync).
        {mailbox === 'inbox'
          ? ' BETA user replies are classified for engagement follow-up; use Reply to respond from contact@optiprime.ai.'
          : ' Outbound mail sent from contact@optiprime.ai via webmail or SMTP.'}
      </p>
      <div className="comms-inbox-toolbar">
        <div className="comms-inbox-sync-row">
          <span className="comms-inbox-sync-meta">
            Last sync: {stats?.lastSync ? formatSyncTimestamp(stats.lastSync) : '—'}
            {' · '}
            {stats?.inboxEmails ?? 0} received · {stats?.sentEmails ?? 0} sent
          </span>
          <button type="button" className="comms-sent-refresh" onClick={() => void runSync()} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync from Porkbun'}
          </button>
        </div>
        {syncMessage && <p className="comms-inbox-sync-msg">{syncMessage}</p>}
        <div className="comms-inbox-mailbox-toggle" role="tablist" aria-label="Mailbox">
          <button
            type="button"
            role="tab"
            aria-selected={mailbox === 'inbox'}
            className={`comms-inbox-mailbox-btn ${mailbox === 'inbox' ? 'active' : ''}`}
            onClick={() => { setMailbox('inbox'); setSelectedId(null); setDetail(null); setPagination((p) => ({ ...p, page: 1 })); }}
          >
            Received
            {counts.inbox?.all != null && <span className="comms-inbox-filter-count">{counts.inbox.all}</span>}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mailbox === 'sent'}
            className={`comms-inbox-mailbox-btn ${mailbox === 'sent' ? 'active' : ''}`}
            onClick={() => { setMailbox('sent'); setSelectedId(null); setDetail(null); setPagination((p) => ({ ...p, page: 1 })); }}
          >
            Sent
            {counts.sent != null && <span className="comms-inbox-filter-count">{counts.sent}</span>}
          </button>
        </div>
        {mailbox === 'inbox' && (
          <div className="comms-inbox-filters" role="tablist" aria-label="Inbox filters">
            {OPTIPRIME_INBOX_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filter === f.id}
                className={`comms-inbox-filter-btn ${filter === f.id ? 'active' : ''}`}
                onClick={() => { setFilter(f.id); setPagination((p) => ({ ...p, page: 1 })); }}
              >
                {f.label}
                {counts.inbox?.[f.id] != null && <span className="comms-inbox-filter-count">{counts.inbox[f.id]}</span>}
              </button>
            ))}
          </div>
        )}
        <form
          className="comms-sent-search-form"
          onSubmit={(e) => { e.preventDefault(); setSearch(searchDraft.trim()); setPagination((p) => ({ ...p, page: 1 })); }}
        >
          <input
            type="search"
            className="comms-sent-search-input"
            placeholder={mailbox === 'sent' ? 'Search subject, recipient, body…' : 'Search subject, sender, body…'}
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
          />
          <button type="submit" className="comms-sent-search-btn">Search</button>
        </form>
      </div>
      {error && <p className="comms-sent-error" role="alert">{error}</p>}
      <div className="comms-sent-layout comms-inbox-layout">
        <div className="comms-sent-list-wrap">
          {loading && emails.length === 0 && <div className="comms-loading">Loading mail…</div>}
          {!loading && emails.length === 0 && (
            <div className="comms-empty">
              No {mailbox === 'sent' ? 'sent' : 'received'} messages here. Click <strong>Sync from Porkbun</strong> to import mail.
            </div>
          )}
          <ul className="comms-sent-list">
            {emails.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className={`comms-sent-row comms-inbox-row ${selectedId === row.id ? 'selected' : ''}`}
                  onClick={() => fetchDetail(row.id)}
                >
                  {mailbox === 'inbox' && (
                    <span className="comms-inbox-row-top">
                      <span className={`comms-inbox-cat comms-inbox-cat-${row.category || 'other'}`}>
                        {inboxCategoryLabel(row.category)}
                      </span>
                      {row.automationHint === 'password_resend' && (
                        <span className="comms-inbox-automation-pip" title="Password resend likely">⚡</span>
                      )}
                    </span>
                  )}
                  {mailbox === 'sent' && (
                    <span className="comms-inbox-row-top">
                      <span className="comms-inbox-cat comms-inbox-cat-sent">Sent</span>
                    </span>
                  )}
                  <span className="comms-sent-row-to">
                    {mailbox === 'sent' ? (row.toEmail || '(no recipient)') : (row.fromName || row.fromEmail)}
                  </span>
                  <span className="comms-sent-row-subj">{row.subject || '(no subject)'}</span>
                  <span className="comms-sent-row-meta">{formatSyncTimestamp(row.receivedAt)}</span>
                </button>
              </li>
            ))}
          </ul>
          {pagination.totalPages > 1 && (
            <div className="comms-sent-pager">
              <button type="button" disabled={pagination.page <= 1 || loading} onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}>Previous</button>
              <span>Page {pagination.page} / {pagination.totalPages}</span>
              <button type="button" disabled={pagination.page >= pagination.totalPages || loading} onClick={() => setPagination((p) => ({ ...p, page: Math.min(p.totalPages, p.page + 1) }))}>Next</button>
            </div>
          )}
        </div>
        <div className="comms-sent-detail-wrap comms-inbox-detail-wrap">
          {!selectedId && (
            <p className="comms-sent-detail-placeholder">
              Select a message to read{mailbox === 'inbox' ? ' and reply' : ''}.
            </p>
          )}
          {selectedId && detailLoading && <div className="comms-loading">Loading message…</div>}
          {selectedId && !detailLoading && detail && (
            <div className="comms-sent-detail comms-inbox-detail">
              <div className="comms-inbox-detail-head">
                <h4>{detail.subject || '(no subject)'}</h4>
                <div className="comms-inbox-detail-actions">
                  {detail.isBetaUser && onComposeToUser && (
                    <button
                      type="button"
                      className="comms-engagement-btn"
                      onClick={() => onComposeToUser(
                        detail.folder === 'SENT' ? detail.toEmail : detail.fromEmail,
                        detail.automationHint === 'password_resend' ? 'tpl_login_01' : 'tpl_engage_01'
                      )}
                    >
                      Open in Compose
                    </button>
                  )}
                  {mailbox === 'sent' && !detail.isBetaUser && onComposeToUser && detail.toEmail && (
                    <button
                      type="button"
                      className="comms-engagement-btn"
                      onClick={() => onComposeToUser(detail.toEmail, 'tpl_engage_01')}
                    >
                      Compose to recipient
                    </button>
                  )}
                  <button type="button" className="comms-engagement-btn" onClick={archiveSelected}>Archive</button>
                </div>
              </div>
              <dl className="comms-sent-detail-dl">
                <dt>From</dt>
                <dd>{detail.fromName ? `${detail.fromName} <${detail.fromEmail}>` : (detail.fromEmail || 'contact@optiprime.ai')}</dd>
                {detail.toEmail && (
                  <>
                    <dt>To</dt>
                    <dd>{detail.toEmail}</dd>
                  </>
                )}
                <dt>{mailbox === 'sent' ? 'Sent' : 'Received'}</dt>
                <dd>{formatSyncTimestamp(detail.receivedAt)}</dd>
                {detail.automationHint && (
                  <>
                    <dt>Automation hint</dt>
                    <dd><code>{detail.automationHint}</code></dd>
                  </>
                )}
              </dl>
              <div className="comms-inbox-body">
                {detail.bodyHtml ? (
                  <iframe
                    title="Email body"
                    className="comms-inbox-body-frame"
                    sandbox=""
                    srcDoc={detail.bodyHtml}
                  />
                ) : (
                  <pre className="comms-sent-detail-body-plain">{detail.bodyText || '—'}</pre>
                )}
              </div>
              {mailbox === 'inbox' && detail.folder !== 'SENT' && (
                <div className="comms-inbox-reply">
                  <label className="cf-label">Reply from contact@optiprime.ai</label>
                  {detail.automationHint && (
                    <p className="comms-inbox-reply-hint">
                      Suggested reply pre-filled for <strong>{detail.automationHint}</strong> — edit before sending.
                    </p>
                  )}
                  <textarea
                    className="comms-inbox-reply-input"
                    rows={8}
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Write your reply…"
                  />
                  <div className="comms-inbox-reply-actions">
                    <button type="button" className="btn btn-primary" onClick={sendReply} disabled={sending || !replyBody.trim()}>
                      {sending ? 'Sending…' : 'Send reply'}
                    </button>
                    <button type="button" className="comms-engagement-btn" onClick={() => setReplyBody(suggestedReplyForEmail(detail))}>
                      Reset suggested reply
                    </button>
                  </div>
                  {replyResult && (
                    <p className={replyResult.ok ? 'send-success' : 'send-error'}>{replyResult.message}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// OptiPrime (contact@optiprime.ai) — IMAP/SMTP via /api/communications/optiprime/*
function OptiPrimeSyncView() {
  const [stats, setStats] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);
  const [configured, setConfigured] = useState(false);

  const fetchStats = () => {
    fetch(`${API}/api/communications/optiprime/config`).then((r) => r.json()).then((d) => d.success && setConfigured(d.configured));
    fetch(`${API}/api/communications/optiprime/stats`).then((r) => r.json()).then((d) => { if (d.success) setStats(d.stats); });
  };
  useEffect(() => { fetchStats(); }, []);

  const triggerSync = () => {
    setSyncing(true);
    setMessage(null);
    fetch(`${API}/api/communications/optiprime/sync`, { method: 'POST' })
      .then((r) => r.json())
      .then((d) => {
        setMessage(d.success ? (d.message || 'Sync completed.') : (d.error || 'Sync failed.'));
        if (d.success) fetchStats();
      })
      .catch((e) => setMessage(e.message || 'Request failed.'))
      .finally(() => setSyncing(false));
  };

  if (!configured) {
    return (
      <div className="optiprime-email-view">
        <p className="optiprime-account-note">
          Configure <strong>contact@optiprime.ai</strong> (Porkbun) by setting <code>OPTIPRIME_EMAIL_PASSWORD</code> in the backend environment. Optional: <code>OPTIPRIME_EMAIL_USER</code>, <code>OPTIPRIME_IMAP_HOST</code>, <code>OPTIPRIME_SMTP_HOST</code>.
        </p>
        <p><a href="https://webmail.porkbun.com" target="_blank" rel="noopener noreferrer" className="comms-ext-link"><i className="fas fa-external-link-alt"></i> Open Porkbun Webmail</a></p>
      </div>
    );
  }
  return (
    <div className="optiprime-sync-view">
      <p className="sync-last">Last synced: {stats?.lastSync || '—'} · Status: {stats?.syncStatus || '—'} · Total in mirror: {stats?.totalEmails ?? '—'}</p>
      <button onClick={triggerSync} disabled={syncing}>{syncing ? 'Syncing...' : 'Sync now'}</button>
      {message && <p className="sync-message">{message}</p>}
      <p className="sync-note">Sync pulls all mail from INBOX and Sent via IMAP into the mirror. Use Browse to view.</p>
      <p className="sync-note optiprime-related-note">
        <strong>Other mail using this address:</strong> transactional email (welcome, BETA applications, etc.) is sent from the onboarding server and may land in
        <strong> Sent</strong> on Porkbun rather than INBOX. The <strong>GMAIL</strong> section on Communications mirrors optiprimebc@gmail.com (labels include outbound from contact@ when classified).
      </p>
    </div>
  );
}

function OptiPrimeSendView({ initialTo = '', initialTemplateId = '', onInitialApplied }) {
  const [toManual, setToManual] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // rawPreviewHtml: the original rich HTML from the last applied template, used for
  // preview only (TipTap normalises/strips complex inline styles so we preserve the
  // raw HTML separately and only clear it when the user explicitly resets the form).
  const [rawPreviewHtml, setRawPreviewHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [configured, setConfigured] = useState(null); // null = checking, true/false = known
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [selectedEmails, setSelectedEmails] = useState(() => new Set());
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [composeTemplates, setComposeTemplates] = useState(() => loadComposeTemplatesFromStorage());
  const [composerLinkedDocs, setComposerLinkedDocs] = useState([]);
  const [appliedTemplateId, setAppliedTemplateId] = useState('');
  const subjectInputRef = useRef(null);
  const prevSelectedRef = useRef(new Set());
  const initialAppliedRef = useRef(false);
  const [aiEnhancing, setAiEnhancing] = useState(false);
  const [aiEnhanceError, setAiEnhanceError] = useState(null);
  /** Snapshot of subject+body before the last successful AI Enhance (saved on send). */
  const [aiOriginalDraft, setAiOriginalDraft] = useState(null);
  const composeAiPollRef = useRef(null);

  useEffect(() => {
    if (initialAppliedRef.current || (!initialTo && !initialTemplateId)) return;
    if (initialTo && initialTo.includes('@')) {
      setToManual(initialTo);
      setSelectedEmails(new Set([initialTo]));
    }
    if (initialTemplateId) {
      if (composeTemplates.length === 0) return;
      const tpl = composeTemplates.find((t) => t.id === initialTemplateId);
      if (!tpl) return;
      const list = buildComposeRecipients(
        initialTo && initialTo.includes('@') ? new Set([initialTo]) : selectedEmails,
        initialTo || toManual
      );
      let sub = tpl.subject || '';
      let bod = tpl.body || '';
      if (list.length === 1) {
        const u = findUserByEmail(users, list[0]);
        sub = applyMergeFieldsToText(sub, u);
        bod = applyMergeFieldsToText(bod, u);
      }
      finishTemplateApply(sub, bod, normalizeTemplateAttachments(tpl), tpl.id);
    }
    initialAppliedRef.current = true;
    if (typeof onInitialApplied === 'function') onInitialApplied();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTo, initialTemplateId, composeTemplates, users]);

  useEffect(() => {
    fetch(`${API}/api/communications/optiprime/config`)
      .then((r) => r.json())
      .then((d) => setConfigured(d.success ? d.configured : false))
      .catch(() => setConfigured(false));
  }, []);

  useEffect(() => {
    setUsersLoading(true);
    fetch(`${API}/api/community/users`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]))
      .finally(() => setUsersLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchComposeTemplatesFromApi()
      .then((d) => {
        if (cancelled) return;
        if (!d.success || !Array.isArray(d.templates)) {
          setComposeTemplates(loadComposeTemplatesFromStorage());
          return;
        }
        let list = d.templates;
        if (list.length === 0) {
          const local = loadComposeTemplatesFromStorage();
          if (local.length > 0) {
            list = local;
            saveComposeTemplatesToServer(local).catch(() => {});
          }
        }
        setComposeTemplates(list);
        persistComposeTemplatesToStorage(list);
      })
      .catch(() => {
        if (!cancelled) setComposeTemplates(loadComposeTemplatesFromStorage());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setToManual((prev) => {
      const prevSel = prevSelectedRef.current;
      const prevTokens = prev.split(/[,;\n]+/).map((s) => s.trim()).filter((s) => s.includes('@'));
      const dropped = [...prevSel].filter((e) => !selectedEmails.has(e));
      const droppedLower = new Set(dropped.map((e) => e.toLowerCase()));
      const kept = prevTokens.filter((e) => !droppedLower.has(e.toLowerCase()));
      const ordered = [];
      const seen = new Set();
      selectedEmails.forEach((e) => {
        const x = e.toLowerCase();
        if (!seen.has(x)) {
          seen.add(x);
          ordered.push(e);
        }
      });
      kept.forEach((e) => {
        const x = e.toLowerCase();
        if (!seen.has(x)) {
          seen.add(x);
          ordered.push(e);
        }
      });
      return ordered.join(', ');
    });
    prevSelectedRef.current = new Set(selectedEmails);
  }, [selectedEmails]);

  useEffect(() => () => {
    if (composeAiPollRef.current) {
      clearInterval(composeAiPollRef.current.intervalId);
      composeAiPollRef.current = null;
    }
  }, []);

  const filtered = users.filter((u) => {
    if (!userEmailEligible(u)) return false;
    if (!recipientSearch.trim()) return true;
    const q = recipientSearch.trim().toLowerCase();
    const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
    return (u.email || '').toLowerCase().includes(q) || name.includes(q) || (u.username || '').toLowerCase().includes(q);
  });

  const toggleOne = (email) => {
    const e = email.trim();
    if (!e) return;
    setSelectedEmails((prev) => { const n = new Set(prev); n.has(e) ? n.delete(e) : n.add(e); return n; });
  };

  const allRecipients = () => buildComposeRecipients(selectedEmails, toManual);

  const mergePreviewUser = useMemo(
    () => resolveMergePreviewUser(users, selectedEmails, toManual),
    [users, selectedEmails, toManual]
  );

  const effectiveComposePreviewUser = useMemo(
    () => (mergePreviewUser && mergePreviewUser.email ? mergePreviewUser : COMPOSE_PREVIEW_SAMPLE_USER),
    [mergePreviewUser]
  );

  const [composeBodyPreviewOpen, setComposeBodyPreviewOpen] = useState(false);

  const finishTemplateApply = (sub, bod, att, templateId = '') => {
    setSubject(sub);
    setBody(bod);
    setRawPreviewHtml(bod);
    setComposerLinkedDocs(Array.isArray(att) ? att : []);
    if (templateId) setAppliedTemplateId(templateId);
    if (bod.length > 400 || /<table[\s>]/i.test(bod)) {
      setComposeBodyPreviewOpen(true);
    }
  };

  const composeRecipientPreviewForEditor = useMemo(() => {
    if (!composeBodyPreviewOpen) return { active: false };
    const usesReal = Boolean(mergePreviewUser && mergePreviewUser.email);
    const nameEmpty =
      usesReal &&
      !String(mergePreviewUser.firstName || '').trim() &&
      !String(mergePreviewUser.lastName || '').trim();
    let hint = usesReal
      ? `Merge fields use your Compose recipient: ${mergePreviewUser.email}.`
      : `Demo recipient (${COMPOSE_PREVIEW_SAMPLE_USER.email}) — put exactly one address in To to preview real merges.`;
    if (nameEmpty) hint += ' Name tokens may be empty (address not in platform list).';
    // Use rawPreviewHtml when available: it is the original rich HTML from the last
    // applied template before TipTap normalises/strips complex inline styles.
    const previewBody = rawPreviewHtml || body || '';
    return {
      active: true,
      srcDoc: buildEmailClientPreviewSrcDoc(applyMergeFieldsToText(previewBody, effectiveComposePreviewUser)),
      subjectLine: applyMergeFieldsToText(subject || '', effectiveComposePreviewUser),
      toLine: formatRecipientPreviewToLine(effectiveComposePreviewUser),
      hint,
    };
  }, [composeBodyPreviewOpen, body, rawPreviewHtml, subject, effectiveComposePreviewUser, mergePreviewUser]);

  const applyTemplateToComposer = (tpl) => {
    if (!tpl) return;
    const list = buildComposeRecipients(selectedEmails, toManual);
    let sub = tpl.subject || '';
    let bod = tpl.body || '';
    if (list.length === 1) {
      const u = findUserByEmail(users, list[0]);
      sub = applyMergeFieldsToText(sub, u);
      bod = applyMergeFieldsToText(bod, u);
    }
    setAiOriginalDraft(null);
    finishTemplateApply(sub, bod, normalizeTemplateAttachments(tpl), tpl.id);
  };

  const stopComposeAiPoll = () => {
    if (composeAiPollRef.current) {
      clearInterval(composeAiPollRef.current.intervalId);
      composeAiPollRef.current = null;
    }
  };

  const applyComposeAiResult = (result) => {
    const nextSubject = result.subject != null ? String(result.subject) : subject;
    const nextBody = result.bodyHtml != null && String(result.bodyHtml).trim()
      ? String(result.bodyHtml).trim()
      : body;
    setSubject(nextSubject);
    setBody(nextBody);
    setRawPreviewHtml(nextBody);
    setComposeBodyPreviewOpen(true);
    setAiEnhanceError(null);
  };

  const startComposeAiEnhance = () => {
    const draftBody = (rawPreviewHtml && String(rawPreviewHtml).trim()) ? rawPreviewHtml : body;
    if (!String(draftBody || '').trim() && !String(subject || '').trim()) {
      setAiEnhanceError('Add a subject or message before AI Enhance.');
      return;
    }
    stopComposeAiPoll();
    setAiEnhancing(true);
    setAiEnhanceError(null);
    // Keep the first pre-enhance draft for the sent log (do not overwrite if already enhanced once)
    if (!aiOriginalDraft) {
      setAiOriginalDraft({
        subject: subject || '',
        body: draftBody || '',
      });
    }
    commsFetch(`${API}/api/communications/compose-templates/ai-enhance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode: 'compose',
        name: 'Compose draft',
        subject: subject || '',
        body: draftBody || '',
      }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok || !d.success || !d.jobId) {
          setAiEnhancing(false);
          setAiEnhanceError(d.error || formatAiEnhanceFailureMessage(null, '', d));
          return;
        }
        const jobId = d.jobId;
        const intervalId = setInterval(() => {
          commsFetch(`${API}/api/communications/compose-templates/ai-enhance/${jobId}`)
            .then((r) => r.json())
            .then((poll) => {
              if (!poll || poll.status === 'pending') return;
              stopComposeAiPoll();
              setAiEnhancing(false);
              if (poll.status === 'complete' && poll.result) {
                applyComposeAiResult(poll.result);
              } else {
                setAiEnhanceError(poll.error || 'AI Enhance failed — check Ollama / Qwen 14B.');
              }
            })
            .catch((e) => {
              stopComposeAiPoll();
              setAiEnhancing(false);
              setAiEnhanceError(e.message || 'AI Enhance poll failed.');
            });
        }, 2500);
        composeAiPollRef.current = { intervalId, jobId };
      })
      .catch((e) => {
        setAiEnhancing(false);
        setAiEnhanceError(e.message || 'AI Enhance request failed.');
      });
  };

  const send = () => {
    const list = allRecipients();
    if (list.length === 0) { setResult({ ok: false, message: 'Add at least one recipient.' }); return; }
    if (!subject.trim() && !window.confirm('Send with an empty subject?')) return;
    setSending(true);
    setResult(null);
    const done = (ok, message) => { setResult({ ok, message }); setSending(false); };
    // Always send the richest available HTML. TipTap normalises/strips complex email HTML
    // (tables, inline gradients) when it loads it; rawPreviewHtml preserves the original.
    const sendBody = (rawPreviewHtml && String(rawPreviewHtml).trim()) ? rawPreviewHtml : body;
    const needsMerge = textContainsMergeFields(subject) || textContainsMergeFields(sendBody);
    const usedAi = Boolean(aiOriginalDraft && String(aiOriginalDraft.body || '').trim());
    const aiMeta = usedAi
      ? { originalBody: aiOriginalDraft.body, aiEnhanced: true }
      : {};
    let endpoint;
    let payload;
    if (list.length === 1) {
      const u = findUserByEmail(users, list[0]);
      endpoint = '/api/communications/optiprime/send';
      payload = {
        to: list[0],
        subject: applyMergeFieldsToText(subject, u),
        body: applyMergeFieldsToText(sendBody, u),
        templateId: appliedTemplateId || undefined,
        ...aiMeta,
      };
    } else if (needsMerge) {
      endpoint = '/api/communications/optiprime/send-bulk';
      payload = {
        templateId: appliedTemplateId || undefined,
        messages: list.map((to) => ({
          to,
          subject: applyMergeFieldsToText(subject, findUserByEmail(users, to)),
          body: applyMergeFieldsToText(sendBody, findUserByEmail(users, to)),
        })),
        ...aiMeta,
      };
    } else {
      endpoint = '/api/communications/optiprime/send-bulk';
      payload = {
        recipients: list,
        subject,
        body: sendBody,
        templateId: appliedTemplateId || undefined,
        ...aiMeta,
      };
    }
    commsFetch(`${API}${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && usedAi) setAiOriginalDraft(null);
        done(!!d.success, d.success ? (d.message || 'Sent!') : d.error);
      })
      .catch((e) => done(false, e.message || 'Request failed.'));
  };

  const recipientCount = allRecipients().length;

  return (
    <div className="compose-page">
      <ComposeTemplatesModal
        open={templatesModalOpen}
        onClose={() => setTemplatesModalOpen(false)}
        templates={composeTemplates}
        onTemplatesChange={setComposeTemplates}
        mergePreviewUser={mergePreviewUser}
        onApplyToComposer={({ subject: sub, body: b, attachments: att, templateId }) => {
          const list = buildComposeRecipients(selectedEmails, toManual);
          let s = sub;
          let bod = b;
          if (list.length === 1) {
            const u = findUserByEmail(users, list[0]);
            s = applyMergeFieldsToText(s, u);
            bod = applyMergeFieldsToText(bod, u);
          }
          finishTemplateApply(s, bod, att, templateId || '');
        }}
      />
      {/* Header strip */}
      <div className="compose-header">
        <div className="compose-header-from-row">
          <div className="compose-from-badge">
            <i className="fas fa-at"></i>
            <span>From: <strong>{OPTIPRIME_SMTP_FROM_LABEL}</strong></span>
          </div>
          <select
            className="compose-template-apply-select"
            aria-label="Apply saved template"
            value={appliedTemplateId}
            onChange={(e) => {
              const id = e.target.value;
              setAppliedTemplateId(id);
              if (!id) return;
              const tpl = composeTemplates.find((t) => t.id === id);
              if (tpl) applyTemplateToComposer(tpl);
            }}
          >
            <option value="">Apply template…</option>
            {composeTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name || 'Untitled'}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="compose-templates-btn"
            onClick={() => setTemplatesModalOpen(true)}
          >
            <i className="fas fa-layer-group"></i>
            Templates
          </button>
          <button
            type="button"
            className="comms-templates-ai-enhance-btn compose-ai-enhance-btn"
            onClick={startComposeAiEnhance}
            disabled={aiEnhancing || sending}
            title="Rewrite and brand this draft with Qwen 14B (Ollama), then review before Send"
          >
            {aiEnhancing ? (
              <><i className="fas fa-spinner fa-spin" aria-hidden /> Enhancing…</>
            ) : (
              <><i className="fas fa-wand-magic-sparkles" aria-hidden /> AI Enhance</>
            )}
          </button>
        </div>
        {aiEnhanceError && (
          <p className="comms-templates-ai-enhance-err compose-ai-enhance-err" role="alert">
            <i className="fas fa-exclamation-circle" /> {aiEnhanceError}
          </p>
        )}
        {aiOriginalDraft && !aiEnhancing && (
          <p className="compose-ai-enhance-ready">
            <i className="fas fa-check-circle" /> AI-enhanced draft ready — Preview shows the branded email. On Send, both the original and enhanced copies are saved.
          </p>
        )}
        {configured === false && (
          <div className="compose-config-warn">
            <i className="fas fa-exclamation-triangle"></i>
            SMTP not configured — set <code>EMAIL_PASSWORD</code> or <code>OPTIPRIME_EMAIL_PASSWORD</code> in the backend environment, then restart <code>dev-optiquery-backend</code>.
          </div>
        )}
      </div>

      <div className="compose-layout">
        {/* ── Left: recipient picker ── */}
        <div className="compose-recipients-panel">
          <div className="crp-header">
            <h4><i className="fas fa-users"></i> Platform users</h4>
            <span className="crp-count">{filtered.length} shown · {selectedEmails.size} selected</span>
          </div>
          <input
            type="search"
            className="crp-search"
            placeholder="Search name, email, username…"
            value={recipientSearch}
            onChange={(e) => setRecipientSearch(e.target.value)}
          />
          <div className="crp-actions">
            <button type="button" onClick={() => setSelectedEmails((prev) => { const n = new Set(prev); filtered.forEach((u) => u.email && n.add(u.email.trim())); return n; })} disabled={filtered.length === 0}>
              Select all
            </button>
            <button type="button" onClick={() => setSelectedEmails(new Set())} disabled={selectedEmails.size === 0}>
              Clear
            </button>
          </div>
          {usersLoading && <p className="crp-loading"><i className="fas fa-spinner fa-spin"></i> Loading…</p>}
          <ul className="crp-list">
            {filtered.map((u) => {
              const em = (u.email || '').trim();
              if (!em) return null;
              const checked = selectedEmails.has(em);
              const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || '';
              const org = (u.organizationName || u.organizationname || '').trim();
              const widgets = Array.isArray(u.allocatedWidgets) ? u.allocatedWidgets : [];
              const roleBits = [];
              if (Array.isArray(u.userTypes) && u.userTypes.length) {
                roleBits.push(...u.userTypes.map((t) => String(t)));
              }
              if (widgets.includes('mayan') && !roleBits.some((r) => /contractor/i.test(r))) {
                roleBits.push('Service Contractor');
              }
              const meta = [org, roleBits.join(' · '), widgets.length ? `widgets: ${widgets.join(', ')}` : '']
                .filter(Boolean)
                .join(' · ');
              return (
                <li
                  key={u.id}
                  className={`crp-row${checked ? ' crp-row-checked' : ''}`}
                  onClick={() => toggleOne(em)}
                  title={`${em}${org ? ` — ${org}` : ''}`}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleOne(em)} onClick={(e) => e.stopPropagation()} />
                  <span className="crp-user-block">
                    <span className="crp-email">{em}</span>
                    {(name || meta) && (
                      <span className="crp-meta">
                        {name && <span className="crp-name">{name}</span>}
                        {meta && <span className="crp-org">{meta}</span>}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
            {!usersLoading && filtered.length === 0 && (
              <li className="crp-empty">No users match</li>
            )}
          </ul>
        </div>

        {/* ── Right: compose form ── */}
        <div className="compose-form-panel">
          <label className="cf-label">To — platform selections sync here; add more addresses after the comma</label>
          <textarea
            className="cf-input"
            rows={2}
            value={toManual}
            onChange={(e) => setToManual(e.target.value)}
            placeholder="Selected users appear automatically. Add investor@example.com, partner@example.com…"
          />

          <div className="cf-label-row">
            <label className="cf-label">Subject</label>
            <MergeFieldInsertControl inputRef={subjectInputRef} value={subject} setValue={setSubject} />
          </div>
          <input
            ref={subjectInputRef}
            className="cf-input"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject line"
          />

          <div className="cf-label-row">
            <label className="cf-label">Message</label>
            {rawPreviewHtml && rawPreviewHtml.length > 400 && !composeBodyPreviewOpen && (
              <span className="compose-rich-template-hint">
                Branded template loaded — use <strong>Preview</strong> to see the full email design.
              </span>
            )}
          </div>
          <ComposeTemplateRichEditor
            content={emailBodyLooksLikeHtml(body) ? body : plainTextToTipTapHtml(body)}
            onChange={setBody}
            mergeFields={COMPOSE_MERGE_FIELDS}
            placeholder="Write your message… Use the toolbar for formatting; paste images inline. Outgoing mail sends as HTML when needed."
            className="cf-rich-editor-outer"
            composeRecipientPreview={composeRecipientPreviewForEditor}
            onComposeRecipientPreviewToggle={() => setComposeBodyPreviewOpen((v) => !v)}
            collapseEditorSurface={composeBodyPreviewOpen}
          />

          {/* Full-panel recipient preview — renders below the editor when Preview is active */}
          {composeBodyPreviewOpen && composeRecipientPreviewForEditor.active && (
            <div className="cf-recipient-preview-panel">
              <div className="cf-recipient-preview-actions">
                <button
                  type="button"
                  className="cf-recipient-preview-close-btn"
                  onClick={() => setComposeBodyPreviewOpen(false)}
                >
                  <i className="fas fa-pen" aria-hidden /> Return to editing
                </button>
                <span className="cf-recipient-preview-mode-label">Preview on — same toggle as Templates</span>
              </div>
              <div className="cf-recipient-preview-chrome">
                <div>
                  <span className="cf-recipient-preview-k">From</span>{' '}
                  <span className="cf-recipient-preview-v">{OPTIPRIME_SMTP_FROM_LABEL}</span>
                </div>
                <div>
                  <span className="cf-recipient-preview-k">To</span>{' '}
                  <span className="cf-recipient-preview-v">{composeRecipientPreviewForEditor.toLine || '—'}</span>
                </div>
                <div>
                  <span className="cf-recipient-preview-k">Subject</span>{' '}
                  <span className="cf-recipient-preview-v">{composeRecipientPreviewForEditor.subjectLine || '(no subject)'}</span>
                </div>
              </div>
              {composeRecipientPreviewForEditor.hint && (
                <p className="cf-recipient-preview-hint">{composeRecipientPreviewForEditor.hint}</p>
              )}
              <iframe
                title="Recipient email preview"
                className="cf-recipient-preview-iframe"
                srcDoc={composeRecipientPreviewForEditor.srcDoc || ''}
              />
              <p className="cf-recipient-preview-foot">
                Use <strong>Return to editing</strong> above or click <strong>Preview</strong> in the toolbar to close preview.
              </p>
            </div>
          )}

          <p className="cf-merge-hint">
            <strong>Merge fields:</strong> use <code>(user_firstname)</code> or <code>{'{user_full_name}'}</code> from the message toolbar. With one recipient, <strong>Apply template</strong> fills them in the composer; with several, tokens resolve per recipient at send time. Use toolbar <strong>Preview</strong> to see the inbox-style render with merges applied.
          </p>
          {composerLinkedDocs.length > 0 && (
            <div className="cf-linked-docs">
              <span className="cf-linked-docs-label">Linked from template (reference only — not attached to outgoing email yet)</span>
              <ul className="cf-linked-docs-list">
                {composerLinkedDocs.map((a) => (
                  <li key={a.linkedDocumentId}>
                    <a
                      href={`${API}/api/opo/auditor/linked-documents/${a.linkedDocumentId}/file`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fas fa-file-alt"></i> {a.document}
                    </a>
                    <button
                      type="button"
                      className="cf-linked-docs-remove"
                      aria-label={`Remove ${a.document} from reference list`}
                      onClick={() => setComposerLinkedDocs((prev) => prev.filter((x) => x.linkedDocumentId !== a.linkedDocumentId))}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="cf-footer">
            <span className="cf-recipient-count">
              {recipientCount === 0
                ? 'No recipients selected yet'
                : recipientCount === 1
                  ? '1 recipient'
                  : `${recipientCount} recipients — sent individually`}
              {aiOriginalDraft ? ' · AI-enhanced (original saved on send)' : ''}
            </span>
            <div className="cf-footer-actions">
              <button
                type="button"
                className="comms-templates-ai-enhance-btn compose-ai-enhance-btn"
                onClick={startComposeAiEnhance}
                disabled={aiEnhancing || sending}
              >
                {aiEnhancing ? (
                  <><i className="fas fa-spinner fa-spin" aria-hidden /> Enhancing…</>
                ) : (
                  <><i className="fas fa-wand-magic-sparkles" aria-hidden /> AI Enhance</>
                )}
              </button>
              <button
                className="cf-send-btn"
                onClick={send}
                disabled={sending || recipientCount === 0 || aiEnhancing}
              >
                {sending ? <><i className="fas fa-spinner fa-spin"></i> Sending…</> : <><i className="fas fa-paper-plane"></i> Send</>}
              </button>
            </div>
          </div>

          {result && (
            <div className={`cf-result ${result.ok ? 'cf-result-ok' : 'cf-result-err'}`}>
              <i className={`fas ${result.ok ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
              {result.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OptiPrimeReplyView() {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [configured, setConfigured] = useState(false);

  const fetchEmails = () => {
    fetch(`${API}/api/communications/optiprime/emails?limit=100`)
      .then((r) => r.json())
      .then((d) => d.success && setEmails(d.emails || []));
  };
  useEffect(() => { fetch(`${API}/api/communications/optiprime/config`).then((r) => r.json()).then((d) => d.success && setConfigured(d.configured)); }, []);
  useEffect(() => { if (configured) fetchEmails(); }, [configured]);

  const sendReply = () => {
    if (!selectedEmail || !replyBody.trim()) return;
    setSending(true);
    setResult(null);
    fetch(`${API}/api/communications/optiprime/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailId: selectedEmail.id, body: replyBody.trim() })
    })
      .then((r) => r.json())
      .then((d) => {
        setResult(d.success ? { ok: true, message: d.message } : { ok: false, message: d.error });
        if (d.success) { setReplyBody(''); setSelectedEmail(null); }
      })
      .catch((e) => setResult({ ok: false, message: e.message }))
      .finally(() => setSending(false));
  };

  if (!configured) return <div className="optiprime-email-view"><p>Set OPTIPRIME_EMAIL_PASSWORD to reply.</p></div>;
  return (
    <div className="optiprime-reply-view">
      <ul className="email-list">
        {emails.map((e) => (
          <li key={e.id} className={`email-row ${selectedEmail?.id === e.id ? 'selected' : ''}`} onClick={() => { setSelectedEmail(e); setResult(null); }}>
            <span className="email-subject">{e.subject || '(no subject)'}</span>
            <span className="email-from">{e.from_email}</span>
          </li>
        ))}
      </ul>
      {selectedEmail && (
        <div className="reply-compose">
          <p><strong>Replying to:</strong> {selectedEmail.from_email} — {selectedEmail.subject}</p>
          <textarea placeholder="Your reply" rows={6} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} />
          <button onClick={sendReply} disabled={sending || !replyBody.trim()}>{sending ? 'Sending...' : 'Send reply'}</button>
          <button type="button" className="secondary" onClick={() => { setSelectedEmail(null); setReplyBody(''); setResult(null); }}>Clear</button>
          {result && <p className={result.ok ? 'result-ok' : 'result-error'}>{result.message}</p>}
        </div>
      )}
    </div>
  );
}

function SyncStatusView() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await commsFetch(`${API}/api/communications/stats?${String(Date.now())}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Stats failed (${res.status})`);
      if (data.success) setStats(data.stats);
      else throw new Error(data.error || 'Could not load stats');
    } catch (e) {
      setSyncMessage(e.message || 'Could not load stats');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  const triggerGmailSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const res = await commsFetch(`${API}/api/communications/sync`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Sync failed');
      setSyncMessage(data.message || 'Gmail sync finished. Stats refreshed below.');
      await loadStats();
    } catch (e) {
      setSyncMessage(e.message || 'Sync request failed.');
    } finally {
      setSyncing(false);
    }
  };

  if (loading && !stats) return <div className="comms-loading">Loading sync status...</div>;
  if (!stats && syncMessage && !loading) {
    return (
      <div className="sync-status-view">
        <p className="comms-error" role="alert">{syncMessage}</p>
        <button type="button" className="btn-sync-primary" onClick={() => void loadStats()}>Retry</button>
      </div>
    );
  }
  if (!stats) return <div className="comms-empty">Could not load stats.</div>;

  return (
    <div className="sync-status-view">
      <div className="sync-status-actions">
        <button
          type="button"
          className="btn-sync-primary"
          onClick={() => void triggerGmailSync()}
          disabled={syncing}
        >
          {syncing ? 'Running Gmail sync…' : 'Run Gmail sync now'}
        </button>
        <button type="button" className="btn-sync-secondary" onClick={() => void loadStats()} disabled={loading || syncing}>
          {loading ? 'Refreshing…' : 'Refresh stats'}
        </button>
      </div>
      {syncMessage && <p className={syncMessage.includes('failed') || syncMessage.includes('Failed') ? 'comms-error' : 'sync-message'} role="status">{syncMessage}</p>}

      <div className="stat-cards">
        <div className="stat-card">
          <h4>Total emails mirrored</h4>
          <p>{stats.totalEmails}</p>
        </div>
        <div className="stat-card">
          <h4>Sync status</h4>
          <p>{stats.syncStatus}</p>
        </div>
        <div className="stat-card stat-card-wide">
          <h4>Last monitor cycle</h4>
          <p className="stat-detail">{formatSyncTimestamp(stats.lastSyncSessionAt)}</p>
          <p className="sync-note subtle">Updated when the Gmail Monitor finishes a 5‑minute cycle (incremental sync + inbox poll). Requires PM2 <code>dev-gmail-monitor</code> with an upgraded DB column (restart monitor after deploy).</p>
        </div>
        <div className="stat-card stat-card-wide">
          <h4>Last full mailbox sync</h4>
          <p className="stat-detail">{formatSyncTimestamp(stats.lastFullSync)}</p>
          <p className="sync-note subtle">First-time full mirror or manual &quot;Run Gmail sync&quot; when history was empty.</p>
        </div>
        <div className="stat-card stat-card-wide">
          <h4>Newest email in mirror</h4>
          <p className="stat-detail">{formatSyncTimestamp(stats.newestMirroredEmailAt)}</p>
          <p className="sync-note subtle">Latest <code>received_at</code> in the database — good sanity check that new mail is arriving.</p>
        </div>
        <div className="stat-card">
          <h4>Pending instructions</h4>
          <p>{stats.pendingInstructions}</p>
        </div>
      </div>
      <p className="sync-note">The Gmail Monitor agent syncs every 5 minutes and processes pending instructions each cycle. Use <strong>Run Gmail sync now</strong> to run <code>run-sync-once.js</code> immediately (same as Inbox → Pull from Gmail).</p>
      <p className="sync-note">If timestamps look stale, confirm PM2: <code>pm2 status dev-gmail-monitor</code> and check <code>node-opo-gmail-monitor</code> logs. Gmail OAuth errors need a fresh <code>GMAIL_REFRESH_TOKEN</code> in <code>.env</code>.</p>
    </div>
  );
}
